import { useState, useEffect, useCallback, useRef } from 'react';
import { db, supabase, uploadFile, deleteFile, BUCKETS } from '../lib/supabase';
import { useAuth } from './useAuth';
import { encryptVaultLink, decryptVaultLink } from '../lib/encrypt';

const fmt = d => { const y=d.getFullYear(), m=String(d.getMonth()+1).padStart(2,'0'), dy=String(d.getDate()).padStart(2,'0'); return `${y}-${m}-${dy}`; };
// `let`, not `const` — kept fresh by useData()'s own effect below so a tab left
// open past midnight doesn't keep computing "today" as yesterday until reload.
export let TODAY = fmt(new Date());
const tempId = () => 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2);
const logErr = (op, err) => { if (err) console.error(`[DB] ${op}:`, err.message || err); };

// ── camelCase → snake_case field mappers ─────────────────────────────────────
// DiaryApp.js uses camelCase internally; Supabase schema uses snake_case
const toGoal = (g) => ({
  ...g,
  start_date:      g.startDate      ?? g.start_date,
  end_date:        g.endDate        ?? g.end_date,
  sub_items:       g.subItems       ?? g.sub_items       ?? [],
  checkin_history: g.checkinHistory ?? g.checkin_history ?? {},
  // remove camelCase keys
  startDate: undefined, endDate: undefined,
  subItems: undefined, checkinHistory: undefined,
});

const fromGoal = (g) => ({
  ...g,
  startDate:      g.start_date,
  endDate:        g.end_date,
  subItems:       g.sub_items       ?? [],
  checkinHistory: g.checkin_history ?? {},
});

const toContentPost = (p) => ({
  ...p,
  pillar_id:      p.pillarId       ?? p.pillar_id,
  scheduled_date: p.scheduledDate  ?? p.scheduled_date,
  pillarId:       undefined, scheduledDate: undefined,
});

const fromContentPost = (p) => ({
  ...p,
  pillarId:      p.pillar_id,
  scheduledDate: p.scheduled_date,
});

const toContentStrategy = (s) => ({
  ...s,
  brand_voice:      s.brandVoice      ?? s.brand_voice,
  target_audience:  s.targetAudience  ?? s.target_audience,
  content_mix:      s.contentMix      ?? s.content_mix      ?? [],
  monthly_goal:     s.monthlyGoal     ?? s.monthly_goal,
  brandVoice:       undefined, targetAudience: undefined,
  contentMix:       undefined, monthlyGoal:    undefined,
});

const fromContentStrategy = (s) => ({
  ...s,
  brandVoice:     s.brand_voice,
  targetAudience: s.target_audience,
  contentMix:     s.content_mix ?? [],
  monthlyGoal:    s.monthly_goal,
});

const toSub = (s) => ({
  ...s,
  // SubTracker uses 'frequency' and 'renewalDate'; older code used 'billingCycle'/'nextBilling'
  billing_cycle: s.billingCycle ?? s.frequency    ?? s.billing_cycle,
  next_billing:  s.nextBilling  ?? s.renewalDate  ?? s.next_billing,
  billingCycle:  undefined, nextBilling: undefined,
  frequency:     undefined, renewalDate: undefined,
});

const fromSub = (s) => ({
  ...s,
  billingCycle: s.billing_cycle,  // legacy camelCase alias
  nextBilling:  s.next_billing,   // legacy camelCase alias
  frequency:    s.billing_cycle,  // SubTracker reads .frequency
  renewalDate:  s.next_billing,   // SubTracker reads .renewalDate
});

const toBook = (b) => ({
  ...b,
  total_pages:   b.totalPages   ?? b.total_pages   ?? 0,
  current_page:  b.currentPage  ?? b.current_page  ?? 0,
  started_date:  b.startDate    ?? b.started_date,
  finished_date: b.finishDate   ?? b.finished_date,
  totalPages:    undefined, currentPage:  undefined,
  startDate:     undefined, finishDate:   undefined,
  entries:       undefined,  // local-only reading log — no DB column
  createdAt:     undefined, updatedAt: undefined,
  created_at:    undefined,
});

const fromBook = (b) => ({
  ...b,
  totalPages:   b.total_pages   ?? 0,
  currentPage:  b.current_page  ?? 0,
  startDate:    b.started_date,
  finishDate:   b.finished_date,
});

const toVision = (v) => ({
  ...v,
  reminder_date: v.reminderDate ?? v.remDate ?? v.reminder_date,
  reminder_time: v.reminderTime ?? v.remTime ?? v.reminder_time,
  image_url:     v.image_url ?? v.image ?? null,
  // Strip all camelCase / local-only fields — DB rejects unknown columns
  reminderDate:  undefined, reminderTime: undefined,
  remDate:       undefined, remTime:      undefined,
  image:         undefined,
  createdAt:     undefined, updatedAt:    undefined,
  created_at:    undefined, updated_at:   undefined,
  pinned_to_dashboard: undefined,  // managed separately
});

const fromVision = (v) => ({
  ...v,
  reminderDate: v.reminder_date,
  reminderTime: v.reminder_time,
  image:        v.image_url ?? v.image ?? null,  // VisionCard reads .image
});

// Expense mapper: DiaryApp form uses 'date', DB column is 'expense_date'
const toExpense = (e) => ({
  ...e,
  expense_date: e.expense_date ?? e.date,
  date: undefined,
});
const fromExpense = (e) => ({
  ...e,
  date: e.expense_date,       // so existing display code reading .date still works
});

// Income mapper: DiaryApp form uses 'date', DB column is 'income_date'
const toIncome = (i) => ({
  ...i,
  income_date: i.income_date ?? i.date,
  date: undefined,
});
const fromIncome = (i) => ({
  ...i,
  date: i.income_date,        // so existing display code reading .date still works
});

// Vault folder mapper: UI uses 'desc', DB column is 'descp'
const toVaultFolder = (f) => ({
  ...f,
  descp: f.descp ?? f.desc,
  desc: undefined,
});
const fromVaultFolder = (f) => ({
  ...f,
  desc: f.descp,              // UI reads .desc
});

// Vault link mapper: UI uses 'desc', DB column is 'descp'
// Sensitive fields (password, sso_password, username, sso_email, otp_value)
// are encrypted with a per-user key before saving and decrypted after loading.
const toVaultLink = (l, uid) => {
  const encrypted = encryptVaultLink(l, uid); // encrypt sensitive fields
  return {
    ...encrypted,
    descp: encrypted.descp ?? encrypted.desc,
    desc: undefined,
  };
};
const fromVaultLink = (l, uid) => {
  const decrypted = decryptVaultLink(l, uid); // decrypt sensitive fields
  return {
    ...decrypted,
    desc: decrypted.descp,              // UI reads .desc
  };
};

// Strip undefined keys before sending to Supabase
const clean = (obj) => {
  const o = {};
  Object.entries(obj).forEach(([k, v]) => { if (v !== undefined) o[k] = v; });
  return o;
};

export function useData() {
  const { user } = useAuth();
  const uid = user?.id;

  const [loading,         setLoading]          = useState(true);
  const [selDate,         setSelDate]           = useState(TODAY);
  const [, tickToday] = useState(0);
  useEffect(() => {
    const checkToday = () => { const t = fmt(new Date()); if (t !== TODAY) { TODAY = t; tickToday(n => n + 1); } };
    const interval = setInterval(checkToday, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') checkToday(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkToday);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', checkToday); };
  }, []);
  const [goals,           setGoals]             = useState([]);
  const [tasks,           setTasks]             = useState({});
  const [dailyEntries,    setDailyEntries]      = useState({});
  const [checkinDefs,     setCheckinDefs]       = useState([]);
  const [dailyCi,         setDailyCi]           = useState({});
  const [habits,          setHabits]            = useState([]);
  const [habitLog,        setHabitLog]          = useState({});
  const [expenses,        setExpenses]          = useState([]);
  const [income,          setIncome]            = useState([]);
  const [ideas,           setIdeas]             = useState([]);
  const [books,           setBooks]             = useState([]);
  const [subs,            setSubs]              = useState([]);
  const [reminders,       setReminders]         = useState([]);
  const [visions,         setVisions]           = useState([]);
  const [visionPillars,   setVisionPillars]     = useState([]);
  const [banner,          setBanner]            = useState({ headline:'', subtext:'', bg_image_url:'' });
  const [dashboardVisions,setDashboardVisions]  = useState([]);
  const [contentPillars,  setContentPillars]    = useState([]);
  const [contentPosts,    setContentPosts]      = useState([]);
  const [contentStrategy, setContentStrategy]   = useState({ brandVoice:'', targetAudience:'', contentMix:[], monthlyGoal:'' });
  const [userFiles,       setUserFiles]         = useState([]);
  const [vaultFolders,    setVaultFolders]      = useState([]);
  const [vaultLinks,      setVaultLinks]        = useState([]);
  const [fixedIncome,     setFixedIncome]       = useState(0);
  const [projects,        setProjects]          = useState([]);
  const [projectTasks,    setProjectTasks]      = useState([]);
  const [projectMembers,  setProjectMembers]    = useState([]);
  const [feedPosts,       setFeedPosts]         = useState([]);
  const [fellows,         setFellows]           = useState([]);
  const [specialDates,    setSpecialDates]      = useState([]);
  const [contentStrategies, setContentStrategies] = useState([]);
  const [recurringTasks,  setRecurringTasks]    = useState([]);
  const [sharedTemplates, setSharedTemplates]   = useState([]);
  // ── New Feature Modules ───────────────────────────────────────────────────
  const [services,    setServices]    = useState([]);
  // SaaS Plans
  const [userPlan,    setUserPlan]    = useState(null);
  const [workspaces,  setWorkspaces]  = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  // Payment Automation
  const [products,    setProducts]    = useState([]);
  const [orders,      setOrders]      = useState([]);
  const [automations, setAutomations] = useState([]);
  const [bizProfile,  setBizProfile]  = useState({});
  const [leads,         setLeads]         = useState([]);
  const [clients,       setClients]       = useState([]);
  const [proposals,     setProposals]     = useState([]);
  const [invoices,      setInvoices]      = useState([]);
  const [sheetLinks,    setSheetLinks]    = useState([]);
  const [consultants,   setConsultants]   = useState([]);
  const [socialAccounts,setSocialAccounts]= useState([]);
  const [dmTemplates,   setDMTemplates]   = useState([]);
  const [viralLinks,    setViralLinks]    = useState([]);
  const [engagement,    setEngagement]    = useState([]);
  const [meetingTypes,  setMeetingTypes]  = useState([]);
  const [availability,  setAvailability]  = useState([]);
  const [meetings,      setMeetings]      = useState([]);
  const [wikiPages,     setWikiPages]     = useState([]);
  const [knowledgeDomains,     setKnowledgeDomains]     = useState([]);
  const [knowledgeCollections, setKnowledgeCollections] = useState([]);
  const [knowledgeTags,        setKnowledgeTags]        = useState([]);
  const [knowledgeItems,       setKnowledgeItems]       = useState([]);
  const [knowledgeItemTags,    setKnowledgeItemTags]    = useState([]);
  const [knowledgeRelated,     setKnowledgeRelated]     = useState([]);
  const [knowledgeAttachments, setKnowledgeAttachments] = useState([]);
  const [integrations,  setIntegrations]  = useState([]);
  const [businessExpenses,  setBusinessExpenses]  = useState([]);
  const [senderProfiles,    setSenderProfiles]    = useState([]);
  const [timeLogs,      setTimeLogs]      = useState([]);
  // ── Tactile Archive new modules ───────────────────────────────────────────
  const [reflections,       setReflections]       = useState([]);
  const [journalEntries,     setJournalEntries]     = useState([]);
  const [journalTags,        setJournalTags]        = useState([]);
  const [journalEntryTags,   setJournalEntryTags]   = useState([]);
  const [journalAttachments, setJournalAttachments] = useState([]);
  const [fitnessMeals,      setFitnessMeals]      = useState([]);
  const [fitnessBodyStats,  setFitnessBodyStats]  = useState([]);
  const [fitnessGoals,      setFitnessGoals]      = useState(null);
  const [dietFoods,           setDietFoods]           = useState([]);
  const [dietMealLogs,        setDietMealLogs]        = useState([]);
  const [dietWaterLogs,       setDietWaterLogs]       = useState([]);
  const [dietSupplements,     setDietSupplements]     = useState([]);
  const [dietSupplementLogs,  setDietSupplementLogs]  = useState([]);
  const [dietTargets,         setDietTargets]         = useState(null);
  const [dietMealPlan,        setDietMealPlan]        = useState([]);
  const [workoutExercises,     setWorkoutExercises]     = useState([]);
  const [workoutPlans,         setWorkoutPlans]         = useState([]);
  const [workoutPlanExercises, setWorkoutPlanExercises] = useState([]);
  const [workoutSchedule,      setWorkoutSchedule]      = useState([]);
  const [workoutLogs,          setWorkoutLogs]          = useState([]);
  const [workoutLogExercises,  setWorkoutLogExercises]  = useState([]);
  const [workoutLogSets,       setWorkoutLogSets]       = useState([]);
  const [bodyProfile,           setBodyProfile]           = useState(null);
  const [bodyCompositionLogs,   setBodyCompositionLogs]   = useState([]);
  const [healthGoals,           setHealthGoals]           = useState([]);
  const [deepWorkSessions,  setDeepWorkSessions]  = useState([]);
  const [calendarEvents,    setCalendarEvents]    = useState([]);
  const debounceTimers = useRef({});

  useEffect(() => {
    if (!uid) { setLoading(false); return; }
    loadAll();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      // Ensure profile row exists
      const { data: prof } = await db.getProfile(uid);
      if (!prof) {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        await supabase.from('profiles').upsert(clean({
          id: uid, email: authUser?.email || '',
          full_name: authUser?.user_metadata?.full_name || '', role: 'user',
        }), { onConflict: 'id' });
        await supabase.from('content_strategy').upsert({ user_id: uid }, { onConflict: 'user_id' });
        await supabase.from('vision_board_banner').upsert({ user_id: uid }, { onConflict: 'user_id' });
        // check-in defaults no longer seeded — user manages via Settings
      } else {
        setFixedIncome(prof.fixed_income || 0);
      }

      const results = await Promise.allSettled([
        db.getGoals(uid),            // 0
        db.getAllTasks(uid),          // 1
        db.getCheckinDefs(uid),      // 2
        db.getHabits(uid),           // 3
        db.getExpenses(uid),         // 4
        db.getIncome(uid),           // 5
        db.getIdeas(uid),            // 6
        db.getBooks(uid),            // 7
        db.getSubs(uid),             // 8
        db.getReminders(uid),        // 9
        db.getVisions(uid),          // 10
        db.getBanner(uid),           // 11
        db.getContentPillars(uid),   // 12
        db.getContentPosts(uid),     // 13
        db.getContentStrategy(uid),  // 14
        db.getFiles(uid),            // 15
        db.getVaultFolders(uid),     // 16
        db.getVaultLinks(uid),       // 17
        db.getProjects(uid),         // 18
        db.getProjectTasks(uid),     // 19
        db.getSpecialDates(uid),     // 20
        db.getContentStrategies(uid),// 21
        db.getRecurringTasks(uid),   // 22
        db.getMyTemplates(uid),      // 23 — own saved templates (goals + calendar)
        db.getProjectMembers(uid),   // 24 — project members where user is owner (we also fetch all below)
        Promise.resolve({data:null}), // 25 — feed posts feature retired (feed_likes has no FK relationship to feed_posts in prod)
        db.getFellows(uid),          // 26 — accountability partners
      ]);

      const get = (i) => results[i].status === 'fulfilled' ? results[i].value?.data : null;

      try { if (get(0))  setGoals(get(0).map(fromGoal)); } catch(e) { console.warn('[load goals]',e); }
      try { if (get(3))  setHabits(get(3)); } catch(e) { console.warn('[load habits]',e); }
      try { if (get(4))  setExpenses(get(4).map(fromExpense)); } catch(e) { console.warn('[load expenses]',e); }
      try { if (get(5))  setIncome(get(5).map(fromIncome)); } catch(e) { console.warn('[load income]',e); }
      try { if (get(6))  setIdeas(get(6)); } catch(e) { console.warn('[load ideas]',e); }
      try {
        if (get(7)) {
          let storedEntries = {};
          try { storedEntries = JSON.parse(localStorage.getItem('diary_book_entries')||'{}'); } catch {}
          setBooks(get(7).map(b => ({ ...fromBook(b), entries: storedEntries[b.id] || [] })));
        }
      } catch(e) { console.warn('[load books]',e); }
      try { if (get(8))  setSubs(get(8).map(fromSub)); } catch(e) { console.warn('[load subs]',e); }
      try { if (get(9))  setReminders(get(9)); } catch(e) { console.warn('[load reminders]',e); }
      try {
        if (get(10)) {
          setVisions(get(10).map(fromVision));
          setDashboardVisions(get(10).filter(v => v.pinned_to_dashboard).map(v => v.id));
        }
      } catch(e) { console.warn('[load visions]',e); }
      try { if (get(11)) setBanner(get(11)); } catch(e) { console.warn('[load banner]',e); }
      try { if (get(12)) setContentPillars(get(12)); } catch(e) { console.warn('[load contentPillars]',e); }
      try { if (get(13)) setContentPosts(get(13).map(fromContentPost)); } catch(e) { console.warn('[load contentPosts]',e); }
      try { if (get(14)) setContentStrategy(fromContentStrategy(get(14))); } catch(e) { console.warn('[load contentStrategy]',e); }
      try { if (get(15)) setUserFiles(get(15)); } catch(e) { console.warn('[load files]',e); }
      try { if (get(16)) setVaultFolders(get(16).map(fromVaultFolder)); } catch(e) { console.warn('[load vaultFolders]',e); }
      try { if (get(17)) setVaultLinks(get(17).map(l => fromVaultLink(l, uid))); } catch(e) { console.warn('[load vaultLinks]',e); }
      try {
        const ownProjects = get(18) || [];
        setProjects(ownProjects); // always set own projects first

        // Load projects where user is an accepted member (user_id set on accept)
        try {
          const userEmail = user?.email || '';
          const [
            { data: memberRows },
            { data: pendingRows },
          ] = await Promise.all([
            db.getMemberProjects(uid),
            userEmail ? db.getPendingInvitesByEmail(userEmail) : Promise.resolve({ data: [] }),
          ]);

          // Auto-accept any pending invites by email (user may have accepted via link)
          if (pendingRows?.length) {
            await Promise.all(pendingRows.map(row =>
              supabase.from('project_members').update({ status: 'accepted', user_id: uid }).eq('id', row.id)
            ));
          }

          // Collect all project IDs: accepted by user_id + just-accepted pending
          const acceptedPids = (memberRows || []).map(r => r.project_id).filter(Boolean);
          const pendingPids  = (pendingRows  || []).map(r => r.project_id).filter(Boolean);
          const allMemberPids = [...new Set([...acceptedPids, ...pendingPids])];

          if (allMemberPids.length) {
            const { data: memberProjData } = await supabase.from('projects').select('*').in('id', allMemberPids);
            const ownIds = new Set(ownProjects.map(p => p.id));
            const extra  = (memberProjData || []).filter(p => !ownIds.has(p.id));
            if (extra.length) setProjects(prev => {
              const ids = new Set(prev.map(p => p.id));
              return [...prev, ...extra.filter(p => !ids.has(p.id))];
            });
          }
        } catch(me) { console.warn('[load member projects]', me.message); }
      } catch(e) { console.warn('[load projects]', e); }
      try {
        const ownTasks = get(19) || [];
        setProjectTasks(ownTasks); // always set own tasks first
        try {
          // Fetch member project IDs same way as project loading above
          const userEmail2 = user?.email || '';
          const [{ data: mRows }, { data: pRows }] = await Promise.all([
            db.getMemberProjects(uid),
            userEmail2 ? db.getPendingInvitesByEmail(userEmail2) : Promise.resolve({ data: [] }),
          ]);
          const ownProjIds   = get(18)?.map(p => p.id) || [];
          const memberPids   = (mRows  || []).map(r => r.project_id).filter(Boolean);
          const pendingPids2 = (pRows  || []).map(r => r.project_id).filter(Boolean);
          const allPids = [...new Set([...ownProjIds, ...memberPids, ...pendingPids2])];
          if (allPids.length) {
            const { data: memberTaskData } = await supabase.from('project_tasks').select('*').in('project_id', allPids);
            if (memberTaskData?.length) {
              setProjectTasks(prev => {
                const ownIds = new Set(prev.map(t => t.id));
                const extra  = memberTaskData.filter(t => !ownIds.has(t.id));
                return extra.length ? [...prev, ...extra] : prev;
              });
            }
          }
        } catch(me) { console.warn('[load member tasks]', me.message); }
      } catch(e) { console.warn('[load projectTasks]', e); }
      try { if (get(20)) setSpecialDates(get(20)); } catch(e) { console.warn('[load specialDates]',e); }
      try { if (get(21)) setContentStrategies(get(21)); } catch(e) { console.warn('[load contentStrategies]',e); }
      try { if (get(22)) setRecurringTasks(get(22)); } catch(e) { console.warn('[load recurringTasks]',e); }
      try { if (get(23)) setSharedTemplates(get(23)); } catch(e) { console.warn('[load sharedTemplates]',e); }
      // ── New Feature Modules ─────────────────────────────────────────────
      // All independent single-table loads below used to run as one sequential
      // await-chain (60+ round trips back to back). Firing them together via
      // Promise.allSettled cuts first-load time to roughly the slowest single
      // request instead of the sum of all of them — same per-call error
      // handling as before, just concurrent instead of serial.
      // Workspaces/workspace_members: tables now exist (023 migration) —
      // load the owner's own member rows here (workspaceMembers used to never
      // load at all, so even successful invites vanished on refresh).
      const workspaceMembersTask = db.getWorkspaceMembers(uid);

      const [batch2, workspaceMembersRes] = await Promise.all([
        Promise.allSettled([
          db.getUserPlan(uid),               // 0
          db.getWorkspaces(uid),               // 1
          Promise.resolve({data:null}), // 2 — getProducts: no `products` table anywhere
          Promise.resolve({data:null}), // 3 — getOrders: no `orders` table anywhere
          Promise.resolve({data:null}), // 4 — getAutomations: no `automations` table anywhere
          db.getServices(uid),                 // 5
          db.getBizProfile(uid),               // 6
          db.getLeads(uid),                    // 7
          db.getClients(uid),                  // 8
          db.getProposals(uid),                // 9
          db.getInvoices(uid),                 // 10
          Promise.resolve({data:null}), // 11 — getSheetLinks: no `sheet_links` table anywhere
          db.getConsultants(uid),              // 12
          Promise.resolve({data:null}), // 13 — getSocialAccounts: no `social_accounts` table anywhere
          Promise.resolve({data:null}), // 14 — getDMTemplates: no `dm_templates` table anywhere
          Promise.resolve({data:null}), // 15 — getViralLinks: no `viral_links` table anywhere
          db.getAllEngagement(uid),             // 16
          Promise.resolve({data:null}), // 17 — getMeetingTypes: no `meeting_types` table anywhere
          db.getAvailability(uid),              // 18
          Promise.resolve({data:null}), // 19 — getMeetings: no `meetings` table anywhere
          db.getWikiPages(uid),                 // 20
          db.getKnowledgeDomains(uid),          // 21
          db.getKnowledgeCollections(uid),      // 22
          db.getKnowledgeTags(uid),             // 23
          db.getKnowledgeItems(uid),            // 24
          db.getKnowledgeItemTags(uid),         // 25
          db.getKnowledgeRelated(uid),          // 26
          db.getKnowledgeAttachments(uid),      // 27
          db.getIntegrations(uid),              // 28
          db.getBusinessExpenses(uid),          // 29
          Promise.resolve({data:null}), // 30 — getSenderProfiles: no `sender_profiles` table anywhere
          db.getTimeLogs(uid),                  // 31
          db.getReflections(uid),               // 32
          db.getJournalEntries(uid),            // 33
          db.getJournalTags(uid),               // 34
          db.getJournalEntryTags(uid),          // 35
          db.getJournalAttachments(uid),        // 36
          db.getFitnessMeals(uid),              // 37
          db.getFitnessBodyStats(uid),          // 38
          db.getFitnessGoals(uid),              // 39
          db.getDietFoods(uid),                 // 40
          db.getDietMealLogs(uid),              // 41
          db.getDietWaterLogs(uid),             // 42
          db.getDietSupplements(uid),           // 43
          db.getDietSupplementLogs(uid),        // 44
          db.getDietTargets(uid),               // 45
          db.getDietMealPlan(uid),              // 46
          db.getWorkoutExercises(uid),          // 47
          db.getWorkoutPlans(uid),              // 48
          db.getWorkoutPlanExercises(uid),      // 49
          db.getWorkoutSchedule(uid),           // 50
          db.getWorkoutLogs(uid),               // 51
          db.getWorkoutLogExercises(uid),       // 52
          db.getWorkoutLogSets(uid),            // 53
          db.getBodyProfile(uid),               // 54
          db.getBodyCompositionLogs(uid),       // 55
          db.getHealthGoals(uid),               // 56
          db.getDeepWorkSessions(uid),          // 57
          db.getCalendarEvents(uid),            // 58
        ]),
        workspaceMembersTask,
      ]);
      const get2 = (i) => batch2[i]?.status === 'fulfilled' ? batch2[i].value?.data : null;

      try { if (workspaceMembersRes?.data) setWorkspaceMembers(workspaceMembersRes.data); } catch(e) { console.warn('[load workspaceMembers]',e); }
      try { if (get2(0))  setUserPlan(get2(0)); } catch(e) { console.warn('[load userPlan]',e); }
      try { if (get2(1))  setWorkspaces(get2(1)); } catch(e) { console.warn('[load workspaces]',e); }
      try { if (get2(2))  setProducts(get2(2)); } catch(e) { console.warn('[load products]',e); }
      try { if (get2(3))  setOrders(get2(3)); } catch(e) { console.warn('[load orders]',e); }
      try { if (get2(4))  setAutomations(get2(4)); } catch(e) { console.warn('[load automations]',e); }
      try { if (get2(5))  setServices(get2(5)); } catch(e) { console.warn('[load services]',e); }
      try { if (get2(6))  setBizProfile(get2(6)); } catch(e) { console.warn('[load bizProfile]',e); }
      try { if (get2(7))  setLeads(get2(7)); } catch(e) { console.warn('[load leads]',e); }
      try { if (get2(8))  setClients(get2(8)); } catch(e) { console.warn('[load clients]',e); }
      try { if (get2(9))  setProposals(get2(9)); } catch(e) { console.warn('[load proposals]',e); }
      try { if (get2(10)) setInvoices(get2(10)); } catch(e) { console.warn('[load invoices]',e); }
      try { if (get2(11)) setSheetLinks(get2(11)); } catch(e) { console.warn('[load sheetLinks]',e); }
      try { if (get2(12)) setConsultants(get2(12)); } catch(e) { console.warn('[load consultants]',e); }
      try { if (get2(13)) setSocialAccounts(get2(13)); } catch(e) { console.warn('[load socialAccounts]',e); }
      try { if (get2(14)) setDMTemplates(get2(14)); } catch(e) { console.warn('[load dmTemplates]',e); }
      try { if (get2(15)) setViralLinks(get2(15)); } catch(e) { console.warn('[load viralLinks]',e); }
      try { if (get2(16)) setEngagement(get2(16)); } catch(e) { console.warn('[load engagement]',e); }
      try { if (get2(17)) setMeetingTypes(get2(17)); } catch(e) { console.warn('[load meetingTypes]',e); }
      try { if (get2(18)) setAvailability(get2(18)); } catch(e) { console.warn('[load availability]',e); }
      try { if (get2(19)) setMeetings(get2(19)); } catch(e) { console.warn('[load meetings]',e); }
      try { if (get2(20)) setWikiPages(get2(20)); } catch(e) { console.warn('[load wiki]',e); }
      try { if (get2(21)) setKnowledgeDomains(get2(21)); } catch(e) { console.warn('[load knowledgeDomains]',e); }
      try { if (get2(22)) setKnowledgeCollections(get2(22)); } catch(e) { console.warn('[load knowledgeCollections]',e); }
      try { if (get2(23)) setKnowledgeTags(get2(23)); } catch(e) { console.warn('[load knowledgeTags]',e); }
      try { if (get2(24)) setKnowledgeItems(get2(24)); } catch(e) { console.warn('[load knowledgeItems]',e); }
      try { if (get2(25)) setKnowledgeItemTags(get2(25)); } catch(e) { console.warn('[load knowledgeItemTags]',e); }
      try { if (get2(26)) setKnowledgeRelated(get2(26)); } catch(e) { console.warn('[load knowledgeRelated]',e); }
      try { if (get2(27)) setKnowledgeAttachments(get2(27)); } catch(e) { console.warn('[load knowledgeAttachments]',e); }
      try { if (get2(28)) setIntegrations(get2(28)); } catch(e) { console.warn('[load integrations]',e); }
      try { if (get2(29)) setBusinessExpenses(get2(29)); } catch(e) { console.warn('[load bizExpenses]',e); }
      try { if (get2(30)) setSenderProfiles(get2(30)); } catch(e) { console.warn('[load senderProfiles]',e); }
      try { if (get2(31)) setTimeLogs(get2(31)); } catch(e) { console.warn('[load timeLogs]',e); }
      try { if (get2(32)) setReflections(get2(32)); } catch(e) { console.warn('[load reflections]',e); }
      try { if (get2(33)) setJournalEntries(get2(33)); } catch(e) { console.warn('[load journalEntries]',e); }
      try { if (get2(34)) setJournalTags(get2(34)); } catch(e) { console.warn('[load journalTags]',e); }
      try { if (get2(35)) setJournalEntryTags(get2(35)); } catch(e) { console.warn('[load journalEntryTags]',e); }
      try { if (get2(36)) setJournalAttachments(get2(36)); } catch(e) { console.warn('[load journalAttachments]',e); }
      try { if (get2(37)) setFitnessMeals(get2(37)); } catch(e) { console.warn('[load fitnessMeals]',e); }
      try { if (get2(38)) setFitnessBodyStats(get2(38)); } catch(e) { console.warn('[load fitnessBodyStats]',e); }
      try { if (get2(39)) setFitnessGoals(get2(39)); } catch(e) { console.warn('[load fitnessGoals]',e); }
      try { if (get2(40)) setDietFoods(get2(40)); } catch(e) { console.warn('[load dietFoods]',e); }
      try { if (get2(41)) setDietMealLogs(get2(41)); } catch(e) { console.warn('[load dietMealLogs]',e); }
      try { if (get2(42)) setDietWaterLogs(get2(42)); } catch(e) { console.warn('[load dietWaterLogs]',e); }
      try { if (get2(43)) setDietSupplements(get2(43)); } catch(e) { console.warn('[load dietSupplements]',e); }
      try { if (get2(44)) setDietSupplementLogs(get2(44)); } catch(e) { console.warn('[load dietSupplementLogs]',e); }
      try { if (get2(45)) setDietTargets(get2(45)); } catch(e) { console.warn('[load dietTargets]',e); }
      try { if (get2(46)) setDietMealPlan(get2(46)); } catch(e) { console.warn('[load dietMealPlan]',e); }
      try { if (get2(47)) setWorkoutExercises(get2(47)); } catch(e) { console.warn('[load workoutExercises]',e); }
      try { if (get2(48)) setWorkoutPlans(get2(48)); } catch(e) { console.warn('[load workoutPlans]',e); }
      try { if (get2(49)) setWorkoutPlanExercises(get2(49)); } catch(e) { console.warn('[load workoutPlanExercises]',e); }
      try { if (get2(50)) setWorkoutSchedule(get2(50)); } catch(e) { console.warn('[load workoutSchedule]',e); }
      try { if (get2(51)) setWorkoutLogs(get2(51)); } catch(e) { console.warn('[load workoutLogs]',e); }
      try { if (get2(52)) setWorkoutLogExercises(get2(52)); } catch(e) { console.warn('[load workoutLogExercises]',e); }
      try { if (get2(53)) setWorkoutLogSets(get2(53)); } catch(e) { console.warn('[load workoutLogSets]',e); }
      try { if (get2(54)) setBodyProfile(get2(54)); } catch(e) { console.warn('[load bodyProfile]',e); }
      try { if (get2(55)) setBodyCompositionLogs(get2(55)); } catch(e) { console.warn('[load bodyCompositionLogs]',e); }
      try { if (get2(56)) setHealthGoals(get2(56)); } catch(e) { console.warn('[load healthGoals]',e); }
      try { if (get2(57)) setDeepWorkSessions(get2(57)); } catch(e) { console.warn('[load deepWorkSessions]',e); }
      try { if (get2(58)) setCalendarEvents(get2(58)); } catch(e) { console.warn('[load calendarEvents]',e); }
      try {
        const rawMembers = get(24) || [];
        if (rawMembers.length) {
          const mids = [...new Set(rawMembers.map(m => m.user_id).filter(Boolean))];
          const { data: memProfiles } = await supabase.from('profiles').select('id,username,full_name,avatar_url').in('id', mids);
          const mpMap = {};
          (memProfiles || []).forEach(p => { mpMap[p.id] = p; });
          setProjectMembers(rawMembers.map(m => ({ ...m, profiles: mpMap[m.user_id] || null })));
        }
      } catch(e) { console.warn('[load projectMembers]',e); }
      try {
        const rawPosts = get(25) || [];
        if (rawPosts.length) {
          // Fetch profiles for post authors
          const pids = [...new Set(rawPosts.map(p => p.user_id).filter(Boolean))];
          const { data: postProfiles } = await supabase.from('profiles').select('id,username,full_name,avatar_url').in('id', pids);
          const ppMap = {};
          (postProfiles || []).forEach(p => { ppMap[p.id] = p; });
          // Mark liked_by_me from feed_likes join data
          setFeedPosts(rawPosts.map(p => ({
            ...p,
            profiles: ppMap[p.user_id] || null,
            liked_by_me: Array.isArray(p.feed_likes) && p.feed_likes.some(l => l.user_id === uid),
            likes_count: Array.isArray(p.feed_likes) ? p.feed_likes.length : (p.likes_count || 0),
          })));
        }
      } catch(e) { console.warn('[load feedPosts]',e); }
      try {
        const rawFellows = get(26) || [];
        if (rawFellows.length) {
          // Enrich with profile data
          const ids = [...new Set(rawFellows.flatMap(f => [f.requester_id, f.addressee_id]).filter(Boolean))];
          const { data: profileData } = await supabase.from('profiles').select('id,username,full_name,avatar_url').in('id', ids);
          const profMap = {};
          (profileData || []).forEach(p => { profMap[p.id] = p; });
          const enriched = rawFellows.map(f => ({
            ...f,
            requester: profMap[f.requester_id] || null,
            addressee: profMap[f.addressee_id] || null,
          }));
          setFellows(enriched);
        }
      } catch(e) { console.warn('[load fellows]',e); }

      const ciDefs = get(2);
      // No hardcoded defaults — user manages their own check-ins via Settings
      if (ciDefs?.length > 0) setCheckinDefs(ciDefs);

      const allTasks = get(1);
      if (allTasks) {
        const map = {};
        // Hydrate status/priority from localStorage (these aren't in DB yet)
        let taskMeta = {}; try { taskMeta = JSON.parse(localStorage.getItem(`diary_task_meta_${uid}`)||'{}'); } catch {}
        allTasks.forEach(t => {
          if (!map[t.task_date]) map[t.task_date] = [];
          // task_time: first check localStorage, then extract from reminder_datetime
          // reminder_datetime may have timezone offset so parse properly
          const metaTime = taskMeta[`${t.id}_task_time`];
          let derivedTime = metaTime || '';
          if (!derivedTime && t.reminder_datetime) {
            // Parse as Date and extract local HH:MM to avoid timezone shift
            // reminder_datetime stored as "YYYY-MM-DDTHH:MM:00" (no tz) — slice is safe
            const raw = t.reminder_datetime;
            // If no timezone suffix, slice directly; if has +/Z, parse as UTC
            if (raw.length >= 16 && !raw.includes('+') && !raw.endsWith('Z')) {
              derivedTime = raw.slice(11, 16); // "HH:MM" directly
            } else {
              // Has timezone — extract time in UTC since we stored in UTC equivalent
              const d = new Date(raw);
              derivedTime = String(d.getUTCHours()).padStart(2,'0') + ':' + String(d.getUTCMinutes()).padStart(2,'0');
            }
          }
          map[t.task_date].push({
            ...t,
            task_time:     derivedTime,
            status:        taskMeta[`${t.id}_status`]        || 'new',
            priority:      taskMeta[`${t.id}_priority`]      || 'normal',
            assignee:      taskMeta[`${t.id}_assignee`]      || '',
            rescheduleDate:taskMeta[`${t.id}_rescheduleDate`]|| '',
            position:      taskMeta[`${t.id}_position`] != null ? Number(taskMeta[`${t.id}_position`]) : 9999,
          });
        });
        setTasks(map);
      }

      // Log any errors
      results.forEach((r, i) => {
        if (r.status === 'rejected') console.warn(`[DB] load[${i}] rejected:`, r.reason);
        else if (r.value?.error) console.warn(`[DB] load[${i}] error:`, r.value.error.message);
      });

    } catch(e) { console.error('[DB] loadAll:', e); }
    setLoading(false);
  }, [uid]);

  // ── VISION PILLARS — loaded separately, fully isolated from main load ────────
  const loadPillars = useCallback(async () => {
    if (!uid) return;
    const DEFAULT_PILLARS = [
      { id:'edu',      label:'Education',          icon:'🎓', color:'#1D5F8A', sort_order:0 },
      { id:'career',   label:'Career & Job',       icon:'💼', color:'#B94040', sort_order:1 },
      { id:'health',   label:'Health & Fitness',   icon:'💪', color:'#2D7A2D', sort_order:2 },
      { id:'relation', label:'Relationships',      icon:'❤️', color:'#C8405A', sort_order:3 },
      { id:'certif',   label:'Certifications',     icon:'🏆', color:'#6B3FA0', sort_order:4 },
      { id:'travel',   label:'Travel & Vacations', icon:'✈️', color:'#0E7490', sort_order:5 },
    ];
    try {
      const { data, error } = await db.getPillars(uid);
      // If table doesn't exist (400/42P01), use defaults silently — no upsert attempt
      if (error) {
        const msg = error.message || '';
        if (msg.includes('relation') || msg.includes('does not exist') || error.code === '42P01' || error.code === 'PGRST200') {
          const stored = (() => { try { return JSON.parse(localStorage.getItem('diary_vision_pillars')); } catch { return null; } })();
          setVisionPillars(stored?.length ? stored : DEFAULT_PILLARS);
          return; // Don't attempt upsert — table doesn't exist
        }
        console.warn('[pillars]', msg);
      }
      if (data?.length) {
        setVisionPillars(data);
        try { localStorage.setItem('diary_vision_pillars', JSON.stringify(data)); } catch {}
      } else if (!error) {
        // Table exists but empty — seed defaults
        const stored = (() => { try { return JSON.parse(localStorage.getItem('diary_vision_pillars')); } catch { return null; } })();
        const defs = stored?.length ? stored : DEFAULT_PILLARS;
        setVisionPillars(defs);
        // Try to save, but silently ignore failures
        defs.forEach((p,i) => Promise.resolve(db.upsertPillar({ ...p, user_id: uid, sort_order: i })).catch(()=>{}));
      }
    } catch(e) { console.warn('[loadPillars]', e); }
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (uid) loadPillars(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const dbSave = (key, fn, delay = 600) => {
    clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(async () => {
      const { error } = await fn();
      logErr(key, error);
    }, delay);
  };

  // ── GOALS ──────────────────────────────────────────────────────────────────
  const addGoal = async (goal) => {
    const tmp = { ...fromGoal({ ...goal }), id: tempId(), user_id: uid };
    setGoals(p => [...p, tmp]);
    const { data, error } = await db.upsertGoal(clean({ ...toGoal(goal), user_id: uid }));
    logErr('addGoal', error);
    if (data?.[0]) setGoals(p => p.map(g => g.id === tmp.id ? fromGoal(data[0]) : g));
  };
  const updateGoal = (id, updates) => {
    setGoals(p => p.map(g => g.id === id ? { ...g, ...updates } : g));
    dbSave(`goal-${id}`, () => db.upsertGoal(clean({ id, user_id: uid, ...toGoal(updates) })));
  };
  const deleteGoal = async (id) => {
    setGoals(p => p.filter(g => g.id !== id));
    logErr('deleteGoal', (await db.deleteGoal(id)).error);
  };

  // ── TASKS ──────────────────────────────────────────────────────────────────
  const addTask = async (date, text, reminderDt, meta={}) => {
    const payload = { user_id: uid, task_date: date, text, done: false, priority: false, reminder_datetime: reminderDt || null };
    const localStatus = meta.status||'new';
    const localPriority = meta.priority||'normal';
    // Derive task_time from reminderDt for immediate display
    const localTime = reminderDt ? reminderDt.slice(11,16) : '';
    const tmp = { ...payload, id: tempId(), status: localStatus, priority: localPriority, task_time: localTime };
    setTasks(p => ({ ...p, [date]: [...(p[date]||[]), tmp] }));
    const { data, error } = await db.upsertTask(payload);
    logErr('addTask', error);
    // After DB save, keep task_time derived from reminder_datetime
    if (data?.[0]) setTasks(p => ({ ...p, [date]: (p[date]||[]).map(t => t.id === tmp.id ? {
      ...data[0],
      status: localStatus,
      priority: localPriority,
      task_time: data[0].reminder_datetime ? data[0].reminder_datetime.slice(11,16) : localTime,
    } : t) }));
  };
  const updateTask = (date, id, updates) => {
    let finalUpdates = { ...updates };
    if ('task_time' in updates) {
      // Build reminder_datetime from task date + new time as a plain ISO string
      // tasks is a map {date: [tasks]} — get the task's stored date
      const allDateTasks = tasks[date] || [];
      const existing = allDateTasks.find(t => t.id === id);
      const taskDate = existing?.task_date || date; // "YYYY-MM-DD"
      const newTime  = updates.task_time;           // "HH:MM" or ""
      // Store as ISO-like string — Supabase accepts "YYYY-MM-DDTHH:MM:00"
      finalUpdates.reminder_datetime = newTime ? `${taskDate}T${newTime}:00` : null;
      finalUpdates.task_time = newTime; // keep in local state too
    }

    setTasks(p => ({ ...p, [date]: (p[date]||[]).map(t => t.id === id ? { ...t, ...finalUpdates } : t) }));

    // Strip fields not in DB schema — task_time is derived, not a DB column
    const { status: _s, priority: _p, assignee: _a, rescheduleDate: _r, position: _pos, task_time: _tt, ...dbUpdates } = finalUpdates;
    if ('status'         in updates) setTaskMeta(id, 'status',        updates.status);
    if ('priority'       in updates) setTaskMeta(id, 'priority',      updates.priority);
    if ('assignee'       in updates) setTaskMeta(id, 'assignee',      updates.assignee);
    if ('rescheduleDate' in updates) setTaskMeta(id, 'rescheduleDate', updates.rescheduleDate);
    if ('position'       in updates) setTaskMeta(id, 'position',      updates.position);
    if ('task_time'      in updates) setTaskMeta(id, 'task_time',     updates.task_time||'');
    // done is a real column — always save it immediately
    if ('done' in dbUpdates && Object.keys(dbUpdates).length > 0) {
      db.updateTask(id, dbUpdates).then(({error}) => { if(error) console.warn('[updateTask]', error); });
    } else if (Object.keys(dbUpdates).length > 0) {
      dbSave(`task-${id}`, () => db.updateTask(id, dbUpdates));
    }
  };

  // ── TASK META (status/priority — persisted in localStorage until DB migration) ─
  const TASK_META_KEY = `diary_task_meta_${uid}`;
  const getTaskMeta = () => { try { return JSON.parse(localStorage.getItem(TASK_META_KEY)||'{}'); } catch { return {}; } };
  const setTaskMeta = (id, field, val) => {
    const m = getTaskMeta(); m[`${id}_${field}`] = val;
    localStorage.setItem(TASK_META_KEY, JSON.stringify(m));
  };
  const deleteTask = async (date, id) => {
    setTasks(p => ({ ...p, [date]: (p[date]||[]).filter(t => t.id !== id) }));
    logErr('deleteTask', (await db.deleteTask(id)).error);
  };

  // ── DAILY ENTRIES ──────────────────────────────────────────────────────────
  const getDailyEntry = (date) => dailyEntries[date] || {};
  const loadDailyEntry = async (date) => {
    if (dailyEntries[date] && Object.keys(dailyEntries[date]).length > 0) return;
    const { data } = await db.getDailyEntry(uid, date);
    if (data) setDailyEntries(p => ({ ...p, [date]: data }));
  };
  const updateDailyEntry = (date, field, value) => {
    setDailyEntries(p => {
      const updated = { ...(p[date]||{}), [field]: value };
      dbSave(`daily-${date}-${field}`, () =>
        db.upsertDailyEntry(clean({ user_id: uid, entry_date: date, ...updated }))
      );
      return { ...p, [date]: updated };
    });
  };

  // ── CHECK-INS ──────────────────────────────────────────────────────────────
  const loadDayCi = async (date) => {
    const { data } = await db.getDailyCheckins(uid, date);
    if (data) {
      const map = {};
      data.forEach(ci => { map[ci.checkin_id] = ci.done; });
      setDailyCi(p => ({ ...p, [date]: map }));
    }
  };
  const toggleCi = async (date, ciId) => {
    const next = !((dailyCi[date]||{})[ciId]);
    setDailyCi(p => ({ ...p, [date]: { ...(p[date]||{}), [ciId]: next } }));
    logErr('toggleCi', (await db.upsertDailyCi({ user_id: uid, checkin_date: date, checkin_id: ciId, done: next })).error);
  };
  const addCheckinDef = async (def) => {
    setCheckinDefs(p => [...p, def]);
    logErr('addCheckinDef', (await db.upsertCheckinDef(clean({ ...def, user_id: uid }))).error);
  };
  const deleteCheckinDef = async (id) => {
    setCheckinDefs(p => p.filter(c => c.id !== id));
    logErr('deleteCheckinDef', (await db.deleteCheckinDef(uid, id)).error);
  };

  // ── HABITS ─────────────────────────────────────────────────────────────────
  const addHabit = async (habit) => {
    const tmp = { ...habit, user_id: uid, id: tempId() };
    setHabits(p => [...p, tmp]);
    const { data, error } = await db.upsertHabit(clean({ ...habit, user_id: uid }));
    logErr('addHabit', error);
    if (data?.[0]) setHabits(p => p.map(h => h.id === tmp.id ? data[0] : h));
  };
  const deleteHabit = async (id) => {
    setHabits(p => p.filter(h => h.id !== id));
    logErr('deleteHabit', (await db.deleteHabit(id)).error);
  };
  const toggleHabit = async (date, habitId) => {
    const key = `${date}:${habitId}`;
    const next = !habitLog[key];
    setHabitLog(p => ({ ...p, [key]: next }));
    logErr('toggleHabit', (await db.upsertHabitLog({ user_id: uid, habit_id: habitId, log_date: date, done: next })).error);

    // Auto-share streak milestones to feed when marking as DONE
    if (next) {
      try {
        const habit = habits.find(h => h.id === habitId);
        if (!habit) return;
        // Count consecutive streak ending on 'date'
        let streak = 0;
        const d = new Date(date + 'T00:00:00');
        const currentLog = { ...habitLog, [key]: true };
        while (true) {
          const ds = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
          if (!currentLog[`${ds}:${habitId}`]) break;
          streak++;
          d.setDate(d.getDate() - 1);
          if (streak > 366) break;
        }
        // Auto-post at milestone streaks: 3, 7, 14, 21, 30, 60, 100, 365
        const MILESTONES = [3, 7, 14, 21, 30, 60, 100, 365];
        if (MILESTONES.includes(streak)) {
          const emoji = streak >= 100 ? '🏆' : streak >= 30 ? '🔥' : streak >= 7 ? '⚡' : '✅';
          const msg = `${emoji} ${streak}-day streak on "${habit.name}"! Consistency is 🔑`;
          await db.insertFeedPost({ user_id: uid, type: 'streak', data: { streak, habit: habit.name, message: msg, milestone: true }, created_at: new Date().toISOString() });
          // Also update local feed state
          setFeedPosts(p => [{ id: Date.now().toString(), user_id: uid, type: 'streak', data: { streak, habit: habit.name, message: msg, milestone: true }, likes_count: 0, created_at: new Date().toISOString(), profiles: null }, ...p]);
        }
      } catch(e) { /* silent — don't block habit toggle */ }
    }
  };
  const loadHabitLogForDate = async (date) => {
    const { data } = await db.getHabitLog(uid, date);
    if (data) {
      const updates = {};
      data.forEach(hl => { updates[`${date}:${hl.habit_id}`] = hl.done; });
      setHabitLog(p => ({ ...p, ...updates }));
    }
  };

  // ── FINANCE ────────────────────────────────────────────────────────────────
  const addExpense = async (exp) => {
    const mapped = toExpense(exp);
    const tmp = { ...fromExpense({ ...mapped }), user_id: uid, id: tempId() };
    setExpenses(p => [tmp, ...p]);
    const { data, error } = await db.upsertExpense(clean({ ...mapped, user_id: uid }));
    logErr('addExpense', error);
    if (data?.[0]) setExpenses(p => p.map(e => e.id === tmp.id ? fromExpense(data[0]) : e));
  };
  const deleteExpense = async (id) => {
    setExpenses(p => p.filter(e => e.id !== id));
    logErr('deleteExpense', (await db.deleteExpense(id)).error);
  };
  const addIncome = async (inc) => {
    const mapped = toIncome(inc);
    const tmp = { ...fromIncome({ ...mapped }), user_id: uid, id: tempId() };
    setIncome(p => [tmp, ...p]);
    const { data, error } = await db.upsertIncome(clean({ ...mapped, user_id: uid }));
    logErr('addIncome', error);
    if (data?.[0]) setIncome(p => p.map(i => i.id === tmp.id ? fromIncome(data[0]) : i));
  };
  const deleteIncome = async (id) => {
    setIncome(p => p.filter(i => i.id !== id));
    logErr('deleteIncome', (await db.deleteIncome(id)).error);
  };
  const updateFixedIncome = (val) => {
    setFixedIncome(val);
    dbSave('fixedIncome', () => db.upsertProfile(clean({ id: uid, fixed_income: val })));
  };

  // ── IDEAS ──────────────────────────────────────────────────────────────────
  const toIdea = (idea) => clean({
    ...idea,
    idea_date:   idea.idea_date ?? idea.date ?? TODAY,  // DB col is idea_date
    description: idea.description ?? idea.desc,          // DB col is description
    date:        undefined,  // no DB column named date
    desc:        undefined,  // no DB column named desc
    createdAt:   undefined,
  });
  const addIdea = async (idea) => {
    const payload = toIdea(idea);
    const tmp = { ...idea, user_id: uid, id: tempId(), created_at: new Date().toISOString() };
    setIdeas(p => [tmp, ...p]);
    const { data, error } = await db.upsertIdea({ ...payload, user_id: uid });
    logErr('addIdea', error);
    if (data?.[0]) setIdeas(p => p.map(i => i.id === tmp.id ? {...data[0], date: data[0].idea_date, desc: data[0].description} : i));
  };
  const updateIdea = (id, updates) => {
    setIdeas(p => p.map(i => i.id === id ? { ...i, ...updates } : i));
    dbSave(`idea-${id}`, () => db.upsertIdea(clean({ id, user_id: uid, name: updates.name, description: updates.description ?? updates.desc, tags: updates.tags, idea_date: TODAY })));
  };
  const deleteIdea = async (id) => {
    setIdeas(p => p.filter(i => i.id !== id));
    logErr('deleteIdea', (await db.deleteIdea(id)).error);
  };

  // ── BOOKS ──────────────────────────────────────────────────────────────────
  const addBook = async (book) => {
    const tmp = { ...fromBook({ ...book }), user_id: uid, id: tempId() };
    setBooks(p => [...p, tmp]);
    const { data, error } = await db.upsertBook(clean({ ...toBook(book), user_id: uid }));
    logErr('addBook', error);
    if (data?.[0]) setBooks(p => p.map(b => b.id === tmp.id ? fromBook(data[0]) : b));
  };
  const updateBook = (id, updates) => {
    setBooks(p => p.map(b => b.id === id ? { ...b, ...updates } : b));
    dbSave(`book-${id}`, () => db.upsertBook(clean({ id, user_id: uid, ...toBook(updates) })));
  };
  const deleteBook = async (id) => {
    setBooks(p => p.filter(b => b.id !== id));
    logErr('deleteBook', (await db.deleteBook(id)).error);
  };

  // ── SUBS ───────────────────────────────────────────────────────────────────
  const addSub = async (sub) => {
    const payload = clean({ ...toSub(sub), user_id: uid });
    const tmp = { ...fromSub({ ...sub }), user_id: uid, id: tempId() };
    setSubs(p => [...p, tmp]);
    const { data, error } = await db.upsertSub(payload);
    logErr('addSub', error);
    if (data?.[0]) setSubs(p => p.map(s => s.id === tmp.id ? fromSub(data[0]) : s));
  };
  const updateSub = (id, updates) => {
    setSubs(p => p.map(s => s.id === id ? { ...s, ...updates } : s));
    dbSave(`sub-${id}`, () => db.upsertSub(clean({ id, user_id: uid, ...toSub(updates) })));
  };
  const deleteSub = async (id) => {
    setSubs(p => p.filter(s => s.id !== id));
    logErr('deleteSub', (await db.deleteSub(id)).error);
  };

  // ── REMINDERS ──────────────────────────────────────────────────────────────
  const addReminder = async (rem) => {
    // Support both datetime and reminder_datetime field names from DiaryApp
    const payload = clean({
      user_id: uid,
      text: rem.text,
      reminder_datetime: rem.reminder_datetime || rem.datetime || null,
      dismissed: false,
    });
    const tmp = { ...payload, id: tempId() };
    setReminders(p => [...p, tmp]);
    const { data, error } = await db.upsertReminder(payload);
    logErr('addReminder', error);
    if (data?.[0]) setReminders(p => p.map(r => r.id === tmp.id ? data[0] : r));
  };
  const dismissReminder = async (id) => {
    setReminders(p => p.map(r => r.id === id ? { ...r, dismissed: true } : r));
    logErr('dismissReminder', (await db.dismissReminder(id)).error);
  };
  const deleteReminder = async (id) => {
    setReminders(p => p.filter(r => r.id !== id));
    logErr('deleteReminder', (await db.deleteReminder(id)).error);
  };

  // ── VISIONS ────────────────────────────────────────────────────────────────
  const addVision = async (vision, imageFile) => {
    let image_url = null;
    if (imageFile) {
      try {
        image_url = await uploadFile(BUCKETS.avatars, `visions/${uid}/${Date.now()}-${imageFile.name}`, imageFile);
      } catch(e) { console.warn('Vision upload:', e.message); }
    }
    const tmp = { ...fromVision({ ...vision }), user_id: uid, id: tempId(), image_url };
    setVisions(p => [...p, tmp]);
    const { data, error } = await db.upsertVision(clean({ ...toVision(vision), user_id: uid, image_url }));
    logErr('addVision', error);
    if (data?.[0]) setVisions(p => p.map(v => v.id === tmp.id ? fromVision(data[0]) : v));
  };
  const updateVision = (id, updates, imageFile) => {
    setVisions(p => p.map(v => v.id === id ? { ...v, ...updates } : v));
    dbSave(`vision-${id}`, async () => {
      let image_url = updates.image_url;
      if (imageFile) {
        try { image_url = await uploadFile(BUCKETS.avatars, `visions/${uid}/${Date.now()}-${imageFile.name}`, imageFile); }
        catch(e) { console.warn('Vision upload:', e.message); }
      }
      return db.upsertVision(clean({ id, user_id: uid, ...toVision(updates), image_url }));
    });
  };
  // ── VISION PILLARS ────────────────────────────────────────────────────────
  const addVisionPillar = async (pillar) => {
    const p = { ...pillar, user_id: uid, sort_order: visionPillars.length };
    setVisionPillars(prev => [...prev, p]);
    const { error } = await db.upsertPillar(p);
    logErr('addVisionPillar', error);
  };
  const updateVisionPillar = (id, updates) => {
    // Re-sort by sort_order after applying — otherwise a drag-reorder (which
    // only changes each pillar's sort_order field) updates the data but the
    // on-screen list keeps its old array order until the next full reload.
    setVisionPillars(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p).sort((a,b)=>(a.sort_order??0)-(b.sort_order??0)));
    db.upsertPillar({ id, user_id: uid, ...updates }).then(({error}) => logErr('updateVisionPillar', error));
  };
  const deleteVisionPillar = async (id) => {
    setVisionPillars(prev => prev.filter(p => p.id !== id));
    const { error } = await db.deletePillar(uid, id);
    logErr('deleteVisionPillar', error);
  };

  const deleteVision = async (id) => {
    setVisions(p => p.filter(v => v.id !== id));
    setDashboardVisions(p => p.filter(x => x !== id));
    logErr('deleteVision', (await db.deleteVision(id)).error);
  };
  const togglePinVision = async (id) => {
    const pinned = !dashboardVisions.includes(id);
    setDashboardVisions(p => pinned ? [...p, id] : p.filter(x => x !== id));
    setVisions(p => p.map(v => v.id === id ? { ...v, pinned_to_dashboard: pinned } : v));
    logErr('togglePin', (await db.upsertVision({ id, user_id: uid, pinned_to_dashboard: pinned })).error);
  };
  const updateBanner = (data) => {
    const norm = { ...data, bg_image_url: data.bg_image_url || data.bgImage || '' };
    delete norm.bgImage;
    setBanner(norm);
    dbSave('banner', () => db.upsertBanner(clean({ ...norm, user_id: uid })));
  };

  // ── CONTENT ────────────────────────────────────────────────────────────────
  const addContentPillar = async (pillar) => {
    const tmp = { ...pillar, user_id: uid, id: tempId() };
    setContentPillars(p => [...p, tmp]);
    const { data, error } = await db.upsertContentPillar(clean({ ...pillar, user_id: uid }));
    logErr('addPillar', error);
    if (data?.[0]) setContentPillars(p => p.map(x => x.id === tmp.id ? data[0] : x));
  };
  const deleteContentPillar = async (id) => {
    setContentPillars(p => p.filter(x => x.id !== id));
    logErr('deletePillar', (await db.deleteContentPillar(id)).error);
  };
  const addContentPost = async (post) => {
    const payload = clean({ ...toContentPost(post), user_id: uid });
    const tmp = { ...fromContentPost({ ...post }), user_id: uid, id: tempId() };
    setContentPosts(p => [tmp, ...p]);
    const { data, error } = await db.upsertContentPost(payload);
    logErr('addPost', error);
    if (data?.[0]) setContentPosts(p => p.map(x => x.id === tmp.id ? fromContentPost(data[0]) : x));
  };
  const updateContentPost = (id, updates) => {
    // Merge — never wipe scheduledDate if not provided in updates
    const normalised = fromContentPost(toContentPost(updates));
    setContentPosts(p => p.map(x => {
      if (x.id !== id) return x;
      // Keep existing scheduledDate/scheduled_date if not in updates
      const merged = { ...x, ...normalised };
      if (!normalised.scheduledDate && x.scheduledDate) merged.scheduledDate = x.scheduledDate;
      if (!normalised.scheduled_date && x.scheduled_date) merged.scheduled_date = x.scheduled_date;
      return merged;
    }));
    // Only send fields that are actually changing — don't null out scheduled_date
    const dbPayload = clean({ id, user_id: uid, ...toContentPost(updates) });
    // Remove scheduled_date from payload if it wasn't in updates to avoid accidental wipe
    if (updates.scheduledDate === undefined && updates.scheduled_date === undefined) {
      delete dbPayload.scheduled_date;
    }
    dbSave(`post-${id}`, () => db.upsertContentPost(dbPayload));
  };
  const deleteContentPost = async (id) => {
    setContentPosts(p => p.filter(x => x.id !== id));
    logErr('deletePost', (await db.deleteContentPost(id)).error);
  };
  const updateContentStrategy = (updates) => {
    setContentStrategy(p => ({ ...p, ...updates }));
    dbSave('cs', () => {
      const merged = { ...contentStrategy, ...updates };
      return db.upsertContentStrategy(clean({ ...toContentStrategy(merged), user_id: uid }));
    });
  };

  // ── FILES ──────────────────────────────────────────────────────────────────
  const uploadUserFile = async (file, entityType, entityId) => {
    const path = `${uid}/${Date.now()}-${file.name}`;
    const url  = await uploadFile(BUCKETS.files, path, file);
    const rec  = { user_id: uid, file_name: file.name, file_url: url, file_size: file.size, file_type: file.type, bucket: BUCKETS.files, entity_type: entityType, entity_id: entityId };
    const { data, error } = await db.insertFile(rec);
    logErr('uploadFile', error);
    if (data?.[0]) setUserFiles(p => [data[0], ...p]);
    return url;
  };
  const deleteUserFile = async (id, fileUrl) => {
    setUserFiles(p => p.filter(f => f.id !== id));
    await db.deleteFileRecord(id);
    try {
      const path = fileUrl.split(`/${BUCKETS.files}/`)[1];
      if (path) await deleteFile(BUCKETS.files, path);
    } catch {}
  };

  // ── LINK VAULT ────────────────────────────────────────────────────────────────
  const addVaultFolder = async (folder) => {
    const mapped = toVaultFolder(folder);
    const tmp = { ...fromVaultFolder({ ...mapped }), user_id: uid, id: tempId() };
    setVaultFolders(p => [...p, tmp]);
    const { data, error } = await db.upsertVaultFolder(clean({ ...mapped, user_id: uid }));
    logErr("addVaultFolder", error);
    if (data?.[0]) setVaultFolders(p => p.map(f => f.id === tmp.id ? fromVaultFolder(data[0]) : f));
  };
  const updateVaultFolder = (id, updates) => {
    setVaultFolders(p => p.map(f => f.id === id ? { ...f, ...updates } : f));
    dbSave(`vf-${id}`, () => db.upsertVaultFolder(clean({ id, user_id: uid, ...toVaultFolder(updates) })));
  };
  const deleteVaultFolder = async (id) => {
    setVaultFolders(p => p.filter(f => f.id !== id));
    logErr("deleteVaultFolder", (await db.deleteVaultFolder(id)).error);
  };
  const addVaultLink = async (link) => {
    const mapped = toVaultLink(link, uid);
    const tmp = { ...fromVaultLink({ ...mapped }, uid), user_id: uid, id: tempId() };
    setVaultLinks(p => [...p, tmp]);
    const { data, error } = await db.upsertVaultLink(clean({ ...mapped, user_id: uid }));
    logErr("addVaultLink", error);
    if (data?.[0]) setVaultLinks(p => p.map(l => l.id === tmp.id ? fromVaultLink(data[0], uid) : l));
  };
  const updateVaultLink = (id, updates) => {
    setVaultLinks(p => p.map(l => l.id === id ? { ...l, ...updates } : l));
    dbSave(`vl-${id}`, () => db.upsertVaultLink(clean({ id, user_id: uid, ...toVaultLink(updates, uid) })));
  };
  const deleteVaultLink = async (id) => {
    setVaultLinks(p => p.filter(l => l.id !== id));
    logErr("deleteVaultLink", (await db.deleteVaultLink(id)).error);
  };

  // ── WEEK PLANS ─────────────────────────────────────────────────────────────
  const [weekPlan,  setWeekPlanState]  = useState({});
  const [monthPlan, setMonthPlanState] = useState({});

  const updateWeekPlan = (wk, updates) => {
    setWeekPlanState(p => ({ ...p, [wk]: { ...(p[wk]||{}), ...updates } }));
    // Persist each field as a separate row (week_plans has day_idx=0 for objective etc)
    Object.entries(updates).forEach(([field, content]) => {
      const dayIdx = { objective:0, topPriority:1, wins:2, learnings:3 }[field] ?? 99;
      dbSave(`wk-${wk}-${field}`, () =>
        db.upsertWeekPlan(clean({ user_id: uid, week_key: wk, day_idx: dayIdx, content: typeof content === 'string' ? content : JSON.stringify(content) }))
      );
    });
  };

  const loadWeekPlan = async (wk) => {
    if (weekPlan[wk]) return;
    const { data } = await db.getWeekPlan(uid, wk);
    if (data?.length) {
      const map = {};
      const fieldNames = ['objective','topPriority','wins','learnings'];
      data.forEach(row => { const f = fieldNames[row.day_idx]; if (f) map[f] = row.content; });
      setWeekPlanState(p => ({ ...p, [wk]: map }));
    }
  };

  const updateMonthPlan = (mk, updates) => {
    setMonthPlanState(p => ({ ...p, [mk]: { ...(p[mk]||{}), ...updates } }));
    Object.entries(updates).forEach(([field, content]) => {
      dbSave(`mo-${mk}-${field}`, () =>
        db.upsertMonthPlan(clean({ user_id: uid, month_key: mk, plan_type: field, content: typeof content === 'string' ? content : JSON.stringify(content) }))
      );
    });
  };

  const loadMonthPlan = async (mk) => {
    if (monthPlan[mk]) return;
    const { data } = await db.getMonthPlan(uid, mk);
    if (data?.length) {
      const map = {};
      data.forEach(row => {
        try { map[row.plan_type] = JSON.parse(row.content); } catch { map[row.plan_type] = row.content; }
      });
      setMonthPlanState(p => ({ ...p, [mk]: map }));
    }
  };

  // ── BACKUP ─────────────────────────────────────────────────────────────────
  const exportBackup = () => {
    const payload = { exportedAt: new Date().toISOString(), version: '2.1', goals, tasks, dailyEntries, checkinDefs, dailyCi, habits, habitLog, expenses, income, ideas, books, subs, reminders, visions, banner, contentPillars, contentPosts, contentStrategy, fixedIncome, projects, projectTasks, specialDates, contentStrategies, recurringTasks };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mydiary-backup-${TODAY}.json`;
    a.click();
  };

  // ── PROJECTS ───────────────────────────────────────────────────────────────
  const addProject = async (proj) => {
    const tmpId = tempId();
    const tmp = { ...proj, id: tmpId, user_id: uid, status: proj.status || 'active', created_at: new Date().toISOString() };
    setProjects(p => [...p, tmp]);
    const { data, error } = await db.upsertProject(clean({ ...proj, user_id: uid }));
    if (error) {
      console.error('[addProject] DB error:', error.message);
      // Remove the optimistic entry on failure
      setProjects(p => p.filter(x => x.id !== tmpId));
      return null;
    }
    if (data?.[0]) setProjects(p => p.map(x => x.id === tmpId ? data[0] : x));
    return data?.[0];
  };
  const updateProject = (id, updates) => {
    setProjects(p => p.map(x => x.id === id ? { ...x, ...updates } : x));
    dbSave(`proj-${id}`, () => db.upsertProject(clean({ id, user_id: uid, ...updates })));
  };
  const deleteProject = async (id) => {
    setProjects(p => p.filter(x => x.id !== id));
    setProjectTasks(p => p.filter(x => x.project_id !== id));
    logErr('deleteProject', (await db.deleteProject(id)).error);
  };

  // ── PROJECT TASKS ──────────────────────────────────────────────────────────
  // Logging is handled by ProjectKanban component directly to avoid duplication

  const addProjectTask = async (task) => {
    const { _addToDaily, ...dbTask } = task;
    const DATE_FIELDS = ['due_date', 'start_date', 'end_date'];
    const TIME_FIELDS = ['due_time', 'start_time', 'end_time'];
    const sanitised = { ...dbTask };
    [...DATE_FIELDS, ...TIME_FIELDS].forEach(f => { if (sanitised[f] === '') delete sanitised[f]; });
    const UUID_FIELDS = ['assignee_id', 'project_id'];
    UUID_FIELDS.forEach(f => { if (sanitised[f] === '' || sanitised[f] === null) delete sanitised[f]; });
    const tmpId = tempId();
    const tmp = { ...sanitised, id: tmpId, user_id: uid, status: sanitised.status || 'todo', created_at: new Date().toISOString() };
    setProjectTasks(p => [...p, tmp]);
    const { data, error } = await db.upsertProjectTask(clean({ ...sanitised, user_id: uid }));
    if (error) {
      console.error('[addProjectTask] DB error:', error.message);
      setProjectTasks(p => p.filter(x => x.id !== tmpId));
      return null;
    }
    if (data?.[0]) {
      setProjectTasks(p => p.map(x => x.id === tmpId ? data[0] : x));
    }
    return data?.[0];
  };

  const updateProjectTask = (id, updates) => {
    const { _addToDaily, ...dbUpdates } = updates;
    const DATE_FIELDS = ['due_date','start_date','end_date'];
    const TIME_FIELDS = ['due_time','start_time','end_time'];
    const sanitised = { ...dbUpdates };
    [...DATE_FIELDS,...TIME_FIELDS].forEach(f => { if (sanitised[f]==='') delete sanitised[f]; });

    setProjectTasks(p => p.map(x => x.id===id ? { ...x, ...sanitised } : x));
    dbSave(`ptask-${id}`, () => db.updateProjectTask(id, sanitised));
  };
  const deleteProjectTask = async (id) => {
    setProjectTasks(p => p.filter(x => x.id !== id));
    logErr('deleteProjectTask', (await db.deleteProjectTask(id)).error);
  };

  // ── SPECIAL DATES ─────────────────────────────────────────────────────────
  const addSpecialDate = async (sd) => {
    const tmp = { ...sd, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setSpecialDates(p => [...p, tmp]);
    const { data, error } = await db.upsertSpecialDate(clean({ ...sd, user_id: uid }));
    logErr('addSpecialDate', error);
    if (data?.[0]) setSpecialDates(p => p.map(x => x.id === tmp.id ? data[0] : x));
  };
  const deleteSpecialDate = async (id) => {
    setSpecialDates(p => p.filter(x => x.id !== id));
    logErr('deleteSpecialDate', (await db.deleteSpecialDate(id)).error);
  };

  // ── HABIT ARCHIVE (soft-delete — preserves log history) ────────────────────
  const archiveHabit = async (id) => {
    setHabits(p => p.map(h => h.id === id ? { ...h, archived: true } : h));
    logErr('archiveHabit', (await db.archiveHabit(id)).error);
  };
  const unarchiveHabit = async (id) => {
    setHabits(p => p.map(h => h.id === id ? { ...h, archived: false } : h));
    logErr('unarchiveHabit', (await db.unarchiveHabit(id)).error);
  };

  // ── GOAL ARCHIVE ──────────────────────────────────────────────────────────
  const archiveGoal = async (id) => {
    setGoals(p => p.map(g => g.id === id ? { ...g, archived: true } : g));
    logErr('archiveGoal', (await db.archiveGoal(id)).error);
  };
  const unarchiveGoal = async (id) => {
    setGoals(p => p.map(g => g.id === id ? { ...g, archived: false } : g));
    logErr('unarchiveGoal', (await db.unarchiveGoal(id)).error);
  };

  // ── BOOK ARCHIVE ──────────────────────────────────────────────────────────
  const archiveBook = async (id) => {
    setBooks(p => p.map(b => b.id === id ? { ...b, archived: true } : b));
    logErr('archiveBook', (await db.archiveBook(id)).error);
  };
  const unarchiveBook = async (id) => {
    setBooks(p => p.map(b => b.id === id ? { ...b, archived: false } : b));
    logErr('unarchiveBook', (await db.unarchiveBook(id)).error);
  };

  // ── RECURRING TASKS ───────────────────────────────────────────────────────
  const addRecurringTask = async (task) => {
    const tmp = { ...task, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setRecurringTasks(p => [...p, tmp]);
    const { data, error } = await db.upsertRecurringTask(clean({ ...task, user_id: uid }));
    logErr('addRecurringTask', error);
    if (data?.[0]) setRecurringTasks(p => p.map(x => x.id === tmp.id ? data[0] : x));
  };
  const updateRecurringTask = (id, updates) => {
    setRecurringTasks(p => p.map(x => x.id === id ? { ...x, ...updates } : x));
    dbSave(`rtask-${id}`, () => db.upsertRecurringTask(clean({ id, user_id: uid, ...updates })));
  };
  const deleteRecurringTask = async (id) => {
    setRecurringTasks(p => p.filter(x => x.id !== id));
    logErr('deleteRecurringTask', (await db.deleteRecurringTask(id)).error);
  };

  // ── SHARED TEMPLATES ──────────────────────────────────────────────────────
  const addSharedTemplate = async (tmpl) => {
    // Always save as is_public:true — the UUID link IS the access control (like Notion)
    const payload = { ...tmpl, is_public: true, user_id: uid };
    const tmp = { ...payload, id: tempId(), created_at: new Date().toISOString() };
    setSharedTemplates(p => [...p, tmp]);
    const { data, error } = await db.upsertSharedTemplate(clean(payload));
    logErr('addSharedTemplate', error);
    if (data?.[0]) setSharedTemplates(p => p.map(x => x.id === tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const deleteSharedTemplate = async (id) => {
    // Soft delete: deactivates the share link (is_public=false) — link stops working immediately
    // Row stays visible in "My Templates" with "🔒 Deactivated" status
    setSharedTemplates(p => p.map(x => x.id === id ? { ...x, is_public: false } : x));
    logErr('softDeleteSharedTemplate', (await supabase.from('shared_templates').update({ is_public: false }).eq('id', id).eq('user_id', uid)).error);
  };
  const hardDeleteSharedTemplate = async (id) => {
    // Hard delete: permanently removes from DB and "My Templates"
    setSharedTemplates(p => p.filter(x => x.id !== id));
    logErr('hardDeleteSharedTemplate', (await db.deleteSharedTemplate(id)).error);
  };
  const fetchPublicTemplates = async (type) => {
    const { data } = await db.getSharedTemplates(type);
    return data || [];
  };
  const fetchMyTemplates = async (type) => {
    const { data } = await db.getMyTemplates(uid);
    const all = data || [];
    setSharedTemplates(all); // refresh state too
    return type ? all.filter(t => t.type === type) : all;
  };
  const fetchTemplateById = async (id) => {
    const { data, error } = await db.getTemplateById(id);
    if (error) console.warn('[fetchTemplateById]', error.message);
    // Increment import count
    if (data) { try { await db.incrementTemplateImport(id); } catch {} }
    return data || null;
  };

  // ── CONTENT STRATEGIES (multiple) ─────────────────────────────────────────
  const addContentStrategy2 = async (strat) => {
    const tmp = { ...strat, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setContentStrategies(p => [...p, tmp]);
    const { data, error } = await db.upsertContentStrategy2(clean({ ...strat, user_id: uid }));
    logErr('addContentStrategy2', error);
    if (data?.[0]) setContentStrategies(p => p.map(x => x.id === tmp.id ? data[0] : x));
  };
  const updateContentStrategy2 = (id, updates) => {
    setContentStrategies(prev => {
      const next = prev.map(x => x.id === id ? { ...x, ...updates } : x);
      const merged = next.find(x => x.id === id);
      if (merged) {
        dbSave(`cstrat-${id}`, () => db.upsertContentStrategy2({
          id: merged.id, user_id: uid,
          name: merged.name || '',
          brand_voice: merged.brand_voice ?? '',
          target_audience: merged.target_audience ?? '',
          monthly_goal: merged.monthly_goal ?? '',
          content_mix: merged.content_mix ?? [],
        }));
      }
      return next;
    });
  };
  const deleteContentStrategy2 = async (id) => {
    setContentStrategies(p => p.filter(x => x.id !== id));
    logErr('deleteContentStrategy2', (await db.deleteContentStrategy2(id)).error);
  };

  // ── PROJECT MEMBERS (collaboration) ────────────────────────────────────────
  const inviteProjectMember = async (projectId, email) => {
    const cleanEmail = email.trim().toLowerCase();
    // Check if already invited
    const existing = projectMembers.find(m => m.project_id === projectId && m.invited_email === cleanEmail);
    if (existing) return { error: { message: `${cleanEmail} is already invited to this project.` } };

    const { data, error } = await db.inviteProjectMember({ project_id: projectId, invited_email: cleanEmail, status: 'pending' });
    if (error && !error.message?.includes('duplicate')) logErr('inviteProjectMember', error);
    if (data?.[0]) {
      setProjectMembers(p => [...p, data[0]]);
      // Send invite email via edge function
      try {
        const project = projects.find(p => p.id === projectId);
        const inviterProfile = await supabase.from('profiles').select('full_name').eq('id', uid).single();
        const inviterName = inviterProfile?.data?.full_name || 'A team member';
        const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpzdnZtZXVkdnl3Y2tkd2xqZWZjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2OTQzMDcsImV4cCI6MjA4ODI3MDMwN30.TI3FmpPcrXYK3wnbJJJP9nT2ehqQbW1ukXdGrI8ZnqE';
        await fetch('https://jsvvmeudvywckdwljefc.supabase.co/functions/v1/send-invite', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${ANON_KEY}`,
            'apikey': ANON_KEY,
          },
          body: JSON.stringify({
            project_id: projectId,
            project_name: project?.name || 'a project',
            invited_email: cleanEmail,
            inviter_name: inviterName,
          }),
        });
      } catch(e) { console.warn('[invite email]', e.message); }
    }
    return data?.[0];
  };
  const acceptProjectInvite = async (memberId) => {
    const { data, error } = await db.updateProjectMember(memberId, { status: 'accepted', user_id: uid });
    logErr('acceptProjectInvite', error);
    if (data?.[0]) setProjectMembers(p => p.map(m => m.id === memberId ? data[0] : m));
  };
  const removeProjectMember = async (memberId) => {
    setProjectMembers(p => p.filter(m => m.id !== memberId));
    logErr('removeProjectMember', (await db.deleteProjectMember(memberId)).error);
  };
  const assignProjectTask = (taskId, assigneeId, assigneeName) => {
    updateProjectTask(taskId, { assignee_id: assigneeId, assignee_name: assigneeName });
  };

  // ── SOCIAL FEED ────────────────────────────────────────────────────────────
  const addFeedPost = async (type, data) => {
    const tmp = { id: tempId(), user_id: uid, type, data, likes_count: 0, created_at: new Date().toISOString() };
    setFeedPosts(p => [tmp, ...p]);
    const { data: saved, error } = await db.insertFeedPost({ user_id: uid, type, data });
    logErr('addFeedPost', error);
    if (saved?.[0]) setFeedPosts(p => p.map(x => x.id === tmp.id ? saved[0] : x));
    return saved?.[0];
  };
  const deleteFeedPost = async (id) => {
    setFeedPosts(p => p.filter(x => x.id !== id));
    logErr('deleteFeedPost', (await db.deleteFeedPost(id)).error);
  };
  const toggleFeedLike = async (postId) => {
    // Optimistic update immediately
    setFeedPosts(prev => prev.map(x => {
      if (x.id !== postId) return x;
      const nowLiked = !x.liked_by_me;
      return { ...x, liked_by_me: nowLiked, likes_count: nowLiked ? (x.likes_count||0)+1 : Math.max(0,(x.likes_count||1)-1) };
    }));
    const { error } = await db.toggleFeedLike(postId, uid);
    if (error) {
      logErr('toggleFeedLike', error);
      // Revert optimistic update on error
      setFeedPosts(prev => prev.map(x => {
        if (x.id !== postId) return x;
        const revert = !x.liked_by_me;
        return { ...x, liked_by_me: revert, likes_count: revert ? (x.likes_count||0)+1 : Math.max(0,(x.likes_count||1)-1) };
      }));
    }
  };

  // ── FELLOWS (accountability partners) ──────────────────────────────────────
  const sendFellowRequest = async (addresseeId) => {
    if (!addresseeId || !uid) return;

    const { data, error } = await db.insertFellow({ requester_id: uid, addressee_id: addresseeId, status: 'pending' });

    if (error) {
      // Already exists — just reload fellows to sync state
      if (error.message?.includes('duplicate') || error.code === '23505') {
        console.log('[fellows] request already exists, reloading');
        const { data: fresh } = await db.getFellows(uid);
        if (fresh?.length) {
          const ids = [...new Set(fresh.flatMap(f => [f.requester_id, f.addressee_id]).filter(Boolean))];
          const { data: profileData } = await supabase.from('profiles').select('id,username,full_name,avatar_url').in('id', ids);
          const profMap = {};
          (profileData || []).forEach(p => { profMap[p.id] = p; });
          setFellows(fresh.map(f => ({
            ...f,
            requester: profMap[f.requester_id] || null,
            addressee: profMap[f.addressee_id] || null,
            requester_name: profMap[f.requester_id]?.full_name || null,
          })));
        }
      } else {
        logErr('sendFellowRequest', error);
      }
      return;
    }

    if (data?.[0]) {
      // Enrich new fellow with requester profile for immediate display
      const { data: myProfile } = await supabase.from('profiles')
        .select('id,username,full_name,avatar_url').eq('id', uid).single()
        .catch(() => ({ data: null }));
      const enrichedFellow = {
        ...data[0],
        requester: myProfile,
        addressee: null,
        requester_name: myProfile?.full_name || null,
      };
      setFellows(p => [...p, enrichedFellow]);

      // Send in-app notification to the addressee so they see it on dashboard
      try {
        await db.insertNotification({
          user_id: addresseeId,
          type: 'fellow_request',
          title: '👥 New Fellow Request',
          message: `${myProfile?.full_name || 'Someone'} sent you a fellow request. Go to Feed → Fellows to accept.`,
          read: false,
          created_at: new Date().toISOString(),
        });
      } catch(e) { console.warn('[fellow notif]', e.message); }
    }
  };
  const respondFellowRequest = async (fellowId, accept) => {
    const status = accept ? 'accepted' : 'rejected';
    const { data, error } = await db.updateFellow(fellowId, { status });
    if (error) { logErr('respondFellowRequest', error); return; }
    if (data?.[0]) {
      // Reload fellows to get fresh enriched data
      const { data: fresh } = await db.getFellows(uid);
      if (fresh?.length) {
        const ids = [...new Set(fresh.flatMap(f => [f.requester_id, f.addressee_id]).filter(Boolean))];
        const { data: profileData } = await supabase.from('profiles').select('id,username,full_name,avatar_url').in('id', ids);
        const profMap = {};
        (profileData || []).forEach(p => { profMap[p.id] = p; });
        setFellows(fresh.map(f => ({
          ...f,
          requester: profMap[f.requester_id] || null,
          addressee: profMap[f.addressee_id] || null,
          requester_name: profMap[f.requester_id]?.full_name || null,
        })));
      } else {
        setFellows([]);
      }
    }
  };
  const removeFellow = async (fellowId) => {
    setFellows(p => p.filter(f => f.id !== fellowId));
    logErr('removeFellow', (await db.deleteFellow(fellowId)).error);
  };
  const searchUserByUsername = async (username) => {
    if (!username?.trim()) return { data: [] };
    const clean = username.replace(/^@/, '').toLowerCase().trim();
    const { data, error } = await db.searchProfiles(clean, uid);
    if (error) console.warn('[searchUserByUsername]', error.message);
    return { data: data || [] };
  };

  // ── SAAS PLAN FUNCTIONS ──────────────────────────────────────────────────
  const upgradePlan = async (planId, paymentData={}) => {
    const d = { plan_id:planId, status:'active', current_period_start:new Date().toISOString(), ...paymentData };
    setUserPlan(p=>({...p,...d,plans:{id:planId}}));
    try { await db.upsertUserPlan(uid, d); } catch(e) { console.warn('[upgradePlan]', e); }
  };

  const addWorkspace = async (ws) => {
    const tmp = { ...ws, id:`tmp-${Date.now()}`, owner_id:uid, created_at:new Date().toISOString() };
    setWorkspaces(p=>[...p,tmp]);
    const { data } = await db.insertWorkspace({...ws, owner_id:uid});
    if (data?.[0]) setWorkspaces(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateWorkspace = (id,u) => { setWorkspaces(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateWorkspace(id,u).then(()=>{}).catch(()=>{}); };
  const deleteWorkspace = async (id) => { setWorkspaces(p=>p.filter(x=>x.id!==id)); await db.deleteWorkspace(id); };

  const inviteWorkspaceMember = async (member) => {
    const tmp = { ...member, id:`tmp-${Date.now()}`, owner_id:uid, status:'pending', invited_at:new Date().toISOString() };
    setWorkspaceMembers(p=>[...p,tmp]);
    const { data, error } = await db.insertWorkspaceMember({...member, owner_id:uid});
    logErr('inviteWorkspaceMember', error);
    if (data?.[0]) setWorkspaceMembers(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateWorkspaceMember = (id,u) => { setWorkspaceMembers(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateWorkspaceMember(id,u).then(()=>{}).catch(()=>{}); };
  const removeWorkspaceMember = async (id) => { setWorkspaceMembers(p=>p.filter(x=>x.id!==id)); await db.deleteWorkspaceMember(id); };

  // ── PAYMENT AUTOMATION FUNCTIONS ─────────────────────────────────────────
  const addProduct = async (prod) => {
    const slug = prod.slug || prod.name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'') + '-' + Date.now().toString(36);
    const tmp = { ...prod, slug, id:`tmp-${Date.now()}`, user_id:uid, is_active:true, total_orders:0, total_revenue:0, created_at:new Date().toISOString() };
    setProducts(p=>[...p,tmp]);
    const {data,error} = await db.insertProduct({...prod,slug,user_id:uid});
    logErr('addProduct',error);
    if (data?.[0]) setProducts(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateProduct = (id,u) => { setProducts(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateProduct(id,u).then(()=>{}).catch(()=>{}); };
  const deleteProduct = async (id) => { setProducts(p=>p.filter(x=>x.id!==id)); await db.deleteProduct(id); };

  const addOrder = async (order) => {
    const tmp = { ...order, id:`tmp-${Date.now()}`, created_at:new Date().toISOString() };
    setOrders(p=>[...p,tmp]);
    const {data,error} = await db.insertOrder(order);
    logErr('addOrder',error);
    if (data?.[0]) setOrders(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateOrder = (id,u) => { setOrders(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateOrder(id,u).then(()=>{}).catch(()=>{}); };

  const addAutomation = async (auto) => {
    const tmp = { ...auto, id:`tmp-${Date.now()}`, user_id:uid, run_count:0, created_at:new Date().toISOString() };
    setAutomations(p=>[...p,tmp]);
    const {data,error} = await db.insertAutomation({...auto,user_id:uid});
    logErr('addAutomation',error);
    if (data?.[0]) setAutomations(p=>p.map(x=>x.id===tmp.id?data[0]:x));
  };
  const updateAutomation = (id,u) => { setAutomations(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateAutomation(id,u).then(()=>{}).catch(()=>{}); };
  const deleteAutomation = async (id) => { setAutomations(p=>p.filter(x=>x.id!==id)); await db.deleteAutomation(id); };

  // ── SERVICES LIBRARY ────────────────────────────────────────────────────
  const addService = async (svc) => {
    const tmp = { ...svc, id: `tmp-${Date.now()}`, user_id: uid, is_active: true, created_at: new Date().toISOString() };
    setServices(p => [...p, tmp]);
    const { data, error } = await db.insertService({ ...svc, user_id: uid });
    logErr('addService', error);
    if (data?.[0]) setServices(p => p.map(x => x.id === tmp.id ? data[0] : x));
  };
  const updateService = (id, u) => { setServices(p => p.map(x => x.id===id?{...x,...u}:x)); db.updateService(id,u).then(()=>{}).catch(()=>{}); };
  const deleteService = async (id) => { setServices(p => p.filter(x => x.id!==id)); await db.deleteService(id); };
  const updateBizProfile = async (updates) => {
    setBizProfile(p => ({...p, ...updates}));
    try {
      const { error } = await db.updateBizProfile(uid, updates);
      if (error) console.error('[updateBizProfile]', error);
    } catch(e) { console.error('[updateBizProfile]', e); }
  };

  // ── BUSINESS MODULE ─────────────────────────────────────────────────────
  const addLead = async (lead, ownerUid) => {
    // ownerUid: for workspace members writing to owner's data
    const targetUid = ownerUid || uid;
    // Sanitize: convert empty date strings to null (Postgres DATE rejects '')
    const clean = Object.fromEntries(Object.entries(lead).map(([k,v]) => [k, v===''?null:v]));
    const tmp = { ...clean, id: `tmp-${Date.now()}`, user_id: targetUid, status: clean.status||'new', created_at: new Date().toISOString() };
    setLeads(p => [...p, tmp]);
    const { data, error } = await db.insertLead({ ...clean, user_id: targetUid });
    logErr('addLead', error);
    if (data?.[0]) setLeads(p => p.map(x => x.id === tmp.id ? data[0] : x));
    return data?.[0];
  };
  const updateLead = (id, updates) => {
    const clean = Object.fromEntries(Object.entries(updates).map(([k,v]) => [k, v===''?null:v]));
    setLeads(p => p.map(x => x.id === id ? { ...x, ...clean } : x));
    db.updateLead(id, clean).then(()=>{}).catch(()=>{});
  };
  const deleteLead = async (id) => {
    setLeads(p => p.filter(x => x.id !== id));
    await db.deleteLead(id);
  };
  const addClient = async (client) => {
    const tmp = { ...client, id: `tmp-${Date.now()}`, user_id: uid, status: 'active', created_at: new Date().toISOString() };
    setClients(p => [...p, tmp]);
    const { data, error } = await db.insertClient({ ...client, user_id: uid });
    logErr('addClient', error);
    if (data?.[0]) setClients(p => p.map(x => x.id === tmp.id ? data[0] : x));
    return data?.[0];
  };
  const updateClient = (id, u) => { setClients(p => p.map(x => x.id===id?{...x,...u}:x)); db.updateClient(id,u).then(()=>{}).catch(()=>{}); };
  const deleteClient = async (id) => { setClients(p=>p.filter(x=>x.id!==id)); await db.deleteClient(id); };
  const addConsultant = async (consultant) => {
    const tmp = { ...consultant, id: `tmp-${Date.now()}`, user_id: uid, status: 'active', created_at: new Date().toISOString() };
    setConsultants(p => [...p, tmp]);
    const { data, error } = await db.insertConsultant({ ...consultant, user_id: uid });
    logErr('addConsultant', error);
    if (data?.[0]) setConsultants(p => p.map(x => x.id === tmp.id ? data[0] : x));
    return data?.[0];
  };
  const updateConsultant = (id, u) => { setConsultants(p => p.map(x => x.id===id?{...x,...u}:x)); db.updateConsultant(id,u).then(()=>{}).catch(()=>{}); };
  const deleteConsultant = async (id) => { setConsultants(p=>p.filter(x=>x.id!==id)); await db.deleteConsultant(id); };
  const convertLeadToClient = async (leadId) => {
    const lead = leads.find(l=>l.id===leadId);
    if (!lead || lead.status==='converted') return;
    updateLead(leadId, { status:'converted' });
    return addClient({ name:lead.name, email:lead.email, phone:lead.phone, company:lead.company, lead_id:leadId });
  };
  const addProposal = async (p) => {
    const items=(p.items||[]).map(({id:_id,...rest})=>rest);
    const sub = items.reduce((s,i)=>s+(parseFloat(i.qty)||1)*(parseFloat(i.rate)||0),0);
    const tax = sub*((parseFloat(p.tax_pct)||18)/100);
    // Strip empty-string UUID fields — Postgres rejects "" for UUID columns
    const sanitize = (v) => (v && v.trim() !== '') ? v : null;
    const shareToken = p.share_token || ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
    const full = {
      ...p, items, subtotal:sub, tax_amount:tax, total:sub+tax,
      status:p.status||'draft', user_id:uid,
      share_token: shareToken,
      client_id:  sanitize(p.client_id),
      lead_id:    sanitize(p.lead_id),
      services_scope: p.services_scope||[],
    };
    const tmp = {...full, id:`tmp-${Date.now()}`, created_at:new Date().toISOString()};
    setProposals(prev=>[...prev,tmp]);
    const {data,error} = await db.insertProposal(full);
    logErr('addProposal',error);
    if (data?.[0]) setProposals(prev=>prev.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateProposal = (id, u) => {
    let fin = {...u};
    if (u.items) { const s=u.items.reduce((a,i)=>a+(parseFloat(i.qty)||1)*(parseFloat(i.rate)||0),0); const tp=u.tax_pct??(proposals.find(x=>x.id===id)?.tax_pct??18); fin={...fin,subtotal:s,tax_amount:s*(tp/100),total:s+s*(tp/100)}; }
    setProposals(p=>p.map(x=>x.id===id?{...x,...fin}:x));
    db.updateProposal(id,fin).then(()=>{}).catch(()=>{});
  };
  const deleteProposal = async (id) => { setProposals(p=>p.filter(x=>x.id!==id)); await db.deleteProposal(id); };
  // Proforma docs used to share the same "INV-" prefix and running sequence as
  // real invoices (this never branched on doc_type at all) — now each doc_type
  // gets its own prefix (from Business Setup) and counts only against its own
  // kind, so proforma numbers don't eat into or skip the real invoice sequence.
  const _nextInvNum = (docType) => {
    const isProforma = docType === 'proforma';
    const prefix = isProforma ? (bizProfile.proforma_prefix || 'PRO') : (bizProfile.inv_prefix || 'INV');
    const relevant = invoices.filter(i => (i.doc_type || 'invoice') === (isProforma ? 'proforma' : 'invoice'));
    const nums = relevant.map(i=>parseInt((i.invoice_number||'0').split('-').pop())||0);
    return `${prefix}-${new Date().getFullYear()}-${String((nums.length?Math.max(...nums):0)+1).padStart(3,'0')}`;
  };
  const addInvoice = async (inv) => {
    const items=(inv.items||[]).map(({id:_id,...rest})=>rest); // strip local id
    const sub=items.reduce((s,i)=>s+(parseFloat(i.qty)||1)*(parseFloat(i.rate)||0),0);
    const tax=sub*((parseFloat(inv.tax_pct)||18)/100);
    const {orientation,...invRest} = inv;
    const sanitize = (v) => (v && String(v).trim() !== '') ? v : null;
    const shareToken = inv.share_token || ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
    const full={...invRest,items,subtotal:sub,tax_amount:tax,total:sub+tax,
      invoice_number:inv.invoice_number||_nextInvNum(inv.doc_type),status:inv.status||'draft',user_id:uid,
      share_token: shareToken,
      client_id: sanitize(inv.client_id),
      proposal_id: sanitize(inv.proposal_id),
    };
    const tmp={...full,id:`tmp-${Date.now()}`,created_at:new Date().toISOString()};
    setInvoices(p=>[...p,tmp]);
    const {data,error}=await db.insertInvoice(full);
    logErr('addInvoice',error);
    if (data?.[0]) setInvoices(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateInvoice = (id, u) => {
    let fin={...u};
    if (u.items){
      const items=u.items.map(({id:_id,...rest})=>rest);
      const s=items.reduce((a,i)=>a+(parseFloat(i.qty)||1)*(parseFloat(i.rate)||0),0);
      const tp=u.tax_pct??(invoices.find(x=>x.id===id)?.tax_pct??18);
      fin={...fin,items,subtotal:s,tax_amount:s*(tp/100),total:s+s*(tp/100)};
    }
    const {orientation,...dbFin}=fin;
    setInvoices(p=>p.map(x=>x.id===id?{...x,...fin}:x));
    db.updateInvoice(id,dbFin).then(()=>{}).catch(()=>{});
  };
  const deleteInvoice = async (id) => { setInvoices(p=>p.filter(x=>x.id!==id)); await db.deleteInvoice(id); };
  const addSheetLink = async (l) => {
    const tmp={...l,id:`tmp-${Date.now()}`,user_id:uid,created_at:new Date().toISOString()};
    setSheetLinks(p=>[...p,tmp]);
    const {data,error}=await db.insertSheetLink({...l,user_id:uid});
    logErr('addSheetLink',error);
    if (data?.[0]) setSheetLinks(p=>p.map(x=>x.id===tmp.id?data[0]:x));
  };
  const updateSheetLink = (id,u) => { setSheetLinks(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateSheetLink(id,u).then(()=>{}).catch(()=>{}); };
  const deleteSheetLink = async (id) => { setSheetLinks(p=>p.filter(x=>x.id!==id)); await db.deleteSheetLink(id); };

  // ── SOCIAL MEDIA ─────────────────────────────────────────────────────────
  const addSocialAccount = async (acc) => {
    const tmp={...acc,id:`tmp-${Date.now()}`,user_id:uid,is_active:true,connected_at:new Date().toISOString()};
    setSocialAccounts(p=>[...p,tmp]);
    const {data,error}=await db.insertSocialAccount({...acc,user_id:uid});
    logErr('addSocialAccount',error);
    if (data?.[0]) setSocialAccounts(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateSocialAccount = (id,u) => { setSocialAccounts(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateSocialAccount(id,u).then(()=>{}).catch(()=>{}); };
  const deleteSocialAccount = async (id) => { setSocialAccounts(p=>p.filter(x=>x.id!==id)); await db.deleteSocialAccount(id); };
  const addDMTemplate = async (t) => {
    const tmp={...t,id:`tmp-${Date.now()}`,user_id:uid,use_count:0,created_at:new Date().toISOString()};
    setDMTemplates(p=>[...p,tmp]);
    const {data,error}=await db.insertDMTemplate({...t,user_id:uid});
    logErr('addDMTemplate',error);
    if (data?.[0]) setDMTemplates(p=>p.map(x=>x.id===tmp.id?data[0]:x));
  };
  const updateDMTemplate = (id,u) => { setDMTemplates(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateDMTemplate(id,u).then(()=>{}).catch(()=>{}); };
  const deleteDMTemplate = async (id) => { setDMTemplates(p=>p.filter(x=>x.id!==id)); await db.deleteDMTemplate(id); };
  const addViralLink = async (l) => {
    const slug=l.slug||(l.cta_keyword||'link').toLowerCase().replace(/\s+/g,'-')+`-${Date.now().toString(36)}`;
    const tmp={...l,slug,id:`tmp-${Date.now()}`,user_id:uid,clicks:0,leads_captured:0,created_at:new Date().toISOString()};
    setViralLinks(p=>[...p,tmp]);
    const {data,error}=await db.insertViralLink({...l,slug,user_id:uid});
    logErr('addViralLink',error);
    if (data?.[0]) setViralLinks(p=>p.map(x=>x.id===tmp.id?data[0]:x));
  };
  const updateViralLink = (id,u) => { setViralLinks(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateViralLink(id,u).then(()=>{}).catch(()=>{}); };
  const deleteViralLink = async (id) => { setViralLinks(p=>p.filter(x=>x.id!==id)); await db.deleteViralLink(id); };
  const saveEngagement = async (postId,metrics) => {
    const entry={...metrics,post_id:postId,user_id:uid,fetched_at:new Date().toISOString()};
    setEngagement(p=>[entry,...p.filter(e=>e.post_id!==postId)]);
    await db.upsertEngagement(entry).then(()=>{}).catch(()=>{});
  };

  // ── MEETING SCHEDULER ────────────────────────────────────────────────────
  const addMeetingType = async (mt) => {
    const tmp={...mt,id:`tmp-${Date.now()}`,user_id:uid,is_active:true,created_at:new Date().toISOString()};
    setMeetingTypes(p=>[...p,tmp]);
    const {data,error}=await db.insertMeetingType({...mt,user_id:uid});
    logErr('addMeetingType',error);
    if (data?.[0]) setMeetingTypes(p=>p.map(x=>x.id===tmp.id?data[0]:x));
  };
  const updateMeetingType = (id,u) => { setMeetingTypes(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateMeetingType(id,u).then(()=>{}).catch(()=>{}); };
  const deleteMeetingType = async (id) => { setMeetingTypes(p=>p.filter(x=>x.id!==id)); await db.deleteMeetingType(id); };
  const saveAvailability = async (slots) => {
    const withUid = slots.map(s=>({...s,user_id:uid}));
    setAvailability(withUid);
    for (const s of withUid) {
      try { await db.upsertAvailability(s); } catch(e) { console.warn('[upsertAvailability]', e); }
    }
  };
  const addMeeting = async (m) => {
    // host_id may come from public booking page (not logged in) — accept it from m.host_id if uid is null
    const hostId = uid || m.host_id;
    if (!hostId) { console.warn('addMeeting: no host_id'); return null; }
    const tmp={...m,id:`tmp-${Date.now()}`,host_id:hostId,status:'confirmed',created_at:new Date().toISOString()};
    if (uid) setMeetings(p=>[...p,tmp]); // only update local state if logged in
    const {data,error}=await db.insertMeeting({...m,host_id:hostId});
    logErr('addMeeting',error);
    if (data?.[0] && uid) setMeetings(p=>p.map(x=>x.id===tmp.id?data[0]:x));
    return data?.[0];
  };
  const updateMeeting = (id,u) => { setMeetings(p=>p.map(x=>x.id===id?{...x,...u}:x)); db.updateMeeting(id,u).then(()=>{}).catch(()=>{}); };
  const deleteMeeting = async (id) => { setMeetings(p=>p.filter(x=>x.id!==id)); await db.deleteMeeting(id); };

  // ── WIKI PAGES ─────────────────────────────────────────────────────────────
  const addWikiPage = async (page) => {
    const tmp = { ...page, id: `tmp-${Date.now()}`, created_at: new Date().toISOString() };
    setWikiPages(p => [...p, tmp]);
    const { data, error } = await db.upsertWikiPage({ ...page, user_id: uid });
    if (error) { console.warn('[addWikiPage]', error); setWikiPages(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setWikiPages(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateWikiPage = (id, updates) => {
    setWikiPages(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.upsertWikiPage({ id, user_id: uid, ...updates, updated_at: new Date().toISOString() })).catch(()=>{});
  };
  const deleteWikiPage = async (id) => {
    setWikiPages(p => p.filter(x => x.id!==id));
    await db.deleteWikiPage(id);
  };

  // ── KNOWLEDGE BASE (Second Brain) ────────────────────────────────────────────
  const addKnowledgeDomain = async (domain) => {
    const tmp = { ...domain, id: `tmp-${Date.now()}`, user_id: uid, created_at: new Date().toISOString() };
    setKnowledgeDomains(p => [...p, tmp]);
    const { data, error } = await db.upsertKnowledgeDomain({ ...domain, user_id: uid });
    if (error) { console.warn('[addKnowledgeDomain]', error); setKnowledgeDomains(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setKnowledgeDomains(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateKnowledgeDomain = (id, updates) => {
    setKnowledgeDomains(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.upsertKnowledgeDomain({ id, user_id: uid, ...updates })).catch(()=>{});
  };
  const deleteKnowledgeDomain = async (id) => {
    setKnowledgeDomains(p => p.filter(x => x.id!==id));
    await db.deleteKnowledgeDomain(id);
  };

  const addKnowledgeCollection = async (collection) => {
    const tmp = { ...collection, id: `tmp-${Date.now()}`, user_id: uid, created_at: new Date().toISOString() };
    setKnowledgeCollections(p => [...p, tmp]);
    const { data, error } = await db.upsertKnowledgeCollection({ ...collection, user_id: uid });
    if (error) { console.warn('[addKnowledgeCollection]', error); setKnowledgeCollections(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setKnowledgeCollections(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateKnowledgeCollection = (id, updates) => {
    setKnowledgeCollections(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.upsertKnowledgeCollection({ id, user_id: uid, ...updates })).catch(()=>{});
  };
  const deleteKnowledgeCollection = async (id) => {
    setKnowledgeCollections(p => p.filter(x => x.id!==id));
    await db.deleteKnowledgeCollection(id);
  };

  const getOrCreateKnowledgeTag = async (name) => {
    const clean = name.trim();
    if (!clean) return null;
    const existing = knowledgeTags.find(t => t.name.toLowerCase() === clean.toLowerCase());
    if (existing) return existing;
    const { data, error } = await db.upsertKnowledgeTag({ user_id: uid, name: clean });
    if (error) { console.warn('[getOrCreateKnowledgeTag]', error); return null; }
    if (data?.[0]) { setKnowledgeTags(p => [...p, data[0]]); return data[0]; }
    return null;
  };
  const deleteKnowledgeTag = async (id) => {
    setKnowledgeTags(p => p.filter(x => x.id!==id));
    setKnowledgeItemTags(p => p.filter(x => x.tag_id!==id));
    await db.deleteKnowledgeTag(id);
  };

  const addKnowledgeItem = async (item) => {
    const tmp = { ...item, id: `tmp-${Date.now()}`, user_id: uid, view_count:0, created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
    setKnowledgeItems(p => [tmp, ...p]);
    const { data, error } = await db.upsertKnowledgeItem({ ...item, user_id: uid });
    if (error) { console.warn('[addKnowledgeItem]', error); setKnowledgeItems(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setKnowledgeItems(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateKnowledgeItem = (id, updates) => {
    setKnowledgeItems(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.upsertKnowledgeItem({ id, user_id: uid, ...updates, updated_at: new Date().toISOString() })).catch(()=>{});
  };
  const deleteKnowledgeItem = async (id) => {
    setKnowledgeItems(p => p.filter(x => x.id!==id));
    setKnowledgeItemTags(p => p.filter(x => x.item_id!==id));
    setKnowledgeRelated(p => p.filter(x => x.item_id!==id && x.related_item_id!==id));
    setKnowledgeAttachments(p => p.filter(x => x.item_id!==id));
    await db.deleteKnowledgeItem(id);
  };
  const recordKnowledgeItemView = (id) => {
    const item = knowledgeItems.find(x => x.id===id);
    if (!item) return;
    const nextCount = (item.view_count||0) + 1;
    setKnowledgeItems(p => p.map(x => x.id===id ? { ...x, view_count: nextCount, last_viewed_at: new Date().toISOString() } : x));
    Promise.resolve(db.touchKnowledgeItemView(id, nextCount)).catch(()=>{});
  };

  const setKnowledgeItemTagList = async (itemId, tagIds) => {
    const current = new Set(knowledgeItemTags.filter(x=>x.item_id===itemId).map(x=>x.tag_id));
    const next = new Set(tagIds);
    const toAdd = [...next].filter(t => !current.has(t));
    const toRemove = [...current].filter(t => !next.has(t));
    setKnowledgeItemTags(p => [
      ...p.filter(x => !(x.item_id===itemId && toRemove.includes(x.tag_id))),
      ...toAdd.map(tagId => ({ item_id: itemId, tag_id: tagId })),
    ]);
    await Promise.all([
      ...toAdd.map(tagId => db.addKnowledgeItemTag(itemId, tagId)),
      ...toRemove.map(tagId => db.removeKnowledgeItemTag(itemId, tagId)),
    ]);
  };

  const addKnowledgeRelated = async (itemId, relatedItemId) => {
    const tmp = { id:`tmp-${Date.now()}`, user_id: uid, item_id: itemId, related_item_id: relatedItemId };
    setKnowledgeRelated(p => [...p, tmp]);
    const { data, error } = await db.addKnowledgeRelated({ user_id: uid, item_id: itemId, related_item_id: relatedItemId });
    if (error) { console.warn('[addKnowledgeRelated]', error); setKnowledgeRelated(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setKnowledgeRelated(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const deleteKnowledgeRelated = async (id) => {
    setKnowledgeRelated(p => p.filter(x => x.id!==id));
    await db.deleteKnowledgeRelated(id);
  };

  const addKnowledgeAttachment = async (attachment) => {
    const { data, error } = await db.addKnowledgeAttachment({ ...attachment, user_id: uid });
    if (error) { console.warn('[addKnowledgeAttachment]', error); return null; }
    if (data?.[0]) setKnowledgeAttachments(p => [...p, data[0]]);
    return data?.[0];
  };
  const deleteKnowledgeAttachment = async (id) => {
    setKnowledgeAttachments(p => p.filter(x => x.id!==id));
    await db.deleteKnowledgeAttachment(id);
  };

  const saveKnowledgeVersion = async (itemId, title, content) => {
    const { data, error } = await db.addKnowledgeVersion({ user_id: uid, item_id: itemId, title, content });
    if (error) { console.warn('[saveKnowledgeVersion]', error); return null; }
    return data?.[0] || null;
  };
  const getKnowledgeVersions = async (itemId) => {
    const { data, error } = await db.getKnowledgeVersions(itemId);
    if (error) { console.warn('[getKnowledgeVersions]', error); return []; }
    return data || [];
  };

  // ── INTEGRATIONS ───────────────────────────────────────────────────────────
  const saveIntegration = async (data) => {
    const { data: saved, error } = await db.upsertIntegration({ ...data, user_id: uid });
    if (!error && saved?.[0]) setIntegrations(p => { const without=p.filter(x=>x.type!==data.type); return [...without, saved[0]]; });
    return saved?.[0];
  };
  const removeIntegration = async (type) => {
    setIntegrations(p => p.filter(x => x.type!==type));
    await db.deleteIntegration(uid, type);
  };

  // ── BUSINESS EXPENSES ──────────────────────────────────────────────────────
  const addBusinessExpense = async (exp) => {
    const tmp = { ...exp, id: `tmp-${Date.now()}`, created_at: new Date().toISOString() };
    setBusinessExpenses(p => [tmp, ...p]);
    const { data, error } = await db.insertBusinessExpense({ ...exp, user_id: uid });
    if (error) { console.warn('[addBusinessExpense]', error); setBusinessExpenses(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setBusinessExpenses(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  // ── SENDER PROFILES ───────────────────────────────────────────────────────
  const addSenderProfile = async (data) => {
    const tmp = { ...data, id: `tmp-${Date.now()}`, user_id: uid };
    setSenderProfiles(p => [...p, tmp]);
    const { data: saved, error } = await db.insertSenderProfile({ ...data, user_id: uid });
    if (error) { setSenderProfiles(p => p.filter(x => x.id !== tmp.id)); return; }
    if (saved?.[0]) setSenderProfiles(p => p.map(x => x.id === tmp.id ? saved[0] : x));
  };
  const updateSenderProfile = (id, updates) => {
    setSenderProfiles(p => p.map(x => x.id === id ? { ...x, ...updates } : x));
    db.updateSenderProfile(id, updates).catch(() => {});
  };
  const deleteSenderProfile = async (id) => {
    setSenderProfiles(p => p.filter(x => x.id !== id));
    await db.deleteSenderProfile(id);
  };

  const deleteBusinessExpense = async (id) => {
    setBusinessExpenses(p => p.filter(x => x.id!==id));
    await db.deleteBusinessExpense(id);
  };

  // ── TIME LOGS ──────────────────────────────────────────────────────────────
  const addTimeLog = async (entry) => {
    const tmp = { ...entry, id: `tmp-${Date.now()}` };
    setTimeLogs(p => [tmp, ...p]);
    const { data, error } = await db.insertTimeLog({ ...entry, user_id: uid });
    if (!error && data?.[0]) setTimeLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };

  // ── REFLECTIONS ────────────────────────────────────────────────────────────
  const addReflection = async (entry) => {
    const tmp = { ...entry, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setReflections(p => [tmp, ...p]);
    const { data, error } = await db.upsertReflection({ ...entry, user_id: uid });
    if (error) { console.warn('[addReflection]', error); setReflections(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setReflections(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateReflection = (id, updates) => {
    setReflections(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    db.upsertReflection({ id, user_id: uid, ...updates }).catch(()=>{});
  };
  const deleteReflection = async (id) => {
    setReflections(p => p.filter(x => x.id!==id));
    await db.deleteReflection(id);
  };

  // ── JOURNAL (Reflection & Journal module) ───────────────────────────────────
  const addJournalEntry = async (entry) => {
    const tmp = { ...entry, id: tempId(), user_id: uid, is_favorite: entry.is_favorite||false, is_pinned: entry.is_pinned||false, status: entry.status||'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
    setJournalEntries(p => [tmp, ...p]);
    const { data, error } = await db.addJournalEntry({ ...entry, user_id: uid });
    if (error) { console.warn('[addJournalEntry]', error); setJournalEntries(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setJournalEntries(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateJournalEntry = (id, updates) => {
    setJournalEntries(p => p.map(x => x.id===id ? { ...x, ...updates, updated_at: new Date().toISOString() } : x));
    Promise.resolve(db.updateJournalEntry(id, updates)).catch(()=>{});
  };
  const deleteJournalEntry = async (id) => {
    setJournalEntries(p => p.filter(x => x.id!==id));
    setJournalEntryTags(p => p.filter(x => x.entry_id!==id));
    setJournalAttachments(p => p.filter(x => x.entry_id!==id));
    await db.deleteJournalEntry(id);
  };

  const getOrCreateJournalTag = async (name) => {
    const clean = name.trim();
    if (!clean) return null;
    const existing = journalTags.find(t => t.name.toLowerCase() === clean.toLowerCase());
    if (existing) return existing;
    const { data, error } = await db.upsertJournalTag({ user_id: uid, name: clean });
    if (error) { console.warn('[getOrCreateJournalTag]', error); return null; }
    if (data?.[0]) { setJournalTags(p => [...p, data[0]]); return data[0]; }
    return null;
  };
  const deleteJournalTag = async (id) => {
    setJournalTags(p => p.filter(x => x.id!==id));
    setJournalEntryTags(p => p.filter(x => x.tag_id!==id));
    await db.deleteJournalTag(id);
  };
  const setJournalEntryTagList = async (entryId, tagIds) => {
    const current = new Set(journalEntryTags.filter(x=>x.entry_id===entryId).map(x=>x.tag_id));
    const next = new Set(tagIds);
    const toAdd = [...next].filter(t => !current.has(t));
    const toRemove = [...current].filter(t => !next.has(t));
    setJournalEntryTags(p => [
      ...p.filter(x => !(x.entry_id===entryId && toRemove.includes(x.tag_id))),
      ...toAdd.map(tagId => ({ entry_id: entryId, tag_id: tagId })),
    ]);
    await Promise.all([
      ...toAdd.map(tagId => db.addJournalEntryTag(entryId, tagId)),
      ...toRemove.map(tagId => db.removeJournalEntryTag(entryId, tagId)),
    ]);
  };

  const addJournalAttachment = async (attachment) => {
    const { data, error } = await db.addJournalAttachment({ ...attachment, user_id: uid });
    if (error) { console.warn('[addJournalAttachment]', error); return null; }
    if (data?.[0]) setJournalAttachments(p => [...p, data[0]]);
    return data?.[0];
  };
  const deleteJournalAttachment = async (id) => {
    setJournalAttachments(p => p.filter(x => x.id!==id));
    await db.deleteJournalAttachment(id);
  };

  const saveJournalVersion = async (entryId, title, content) => {
    const { data, error } = await db.addJournalVersion({ user_id: uid, entry_id: entryId, title, content });
    if (error) { console.warn('[saveJournalVersion]', error); return null; }
    return data?.[0] || null;
  };
  const getJournalVersions = async (entryId) => {
    const { data, error } = await db.getJournalVersions(entryId);
    if (error) { console.warn('[getJournalVersions]', error); return []; }
    return data || [];
  };

  // ── FITNESS & DIET ─────────────────────────────────────────────────────────
  const addFitnessMeal = async (meal) => {
    const tmp = { ...meal, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setFitnessMeals(p => [tmp, ...p]);
    const { data, error } = await db.insertFitnessMeal({ ...meal, user_id: uid });
    if (error) { console.warn('[addFitnessMeal]', error); setFitnessMeals(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setFitnessMeals(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteFitnessMeal = async (id) => {
    setFitnessMeals(p => p.filter(x => x.id!==id));
    await db.deleteFitnessMeal(id);
  };
  const addFitnessBodyStat = async (stat) => {
    const tmp = { ...stat, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setFitnessBodyStats(p => [tmp, ...p]);
    const { data, error } = await db.upsertFitnessBodyStat({ ...stat, user_id: uid });
    if (error) { console.warn('[addFitnessBodyStat]', error); setFitnessBodyStats(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setFitnessBodyStats(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteFitnessBodyStat = async (id) => {
    setFitnessBodyStats(p => p.filter(x => x.id!==id));
    await db.deleteFitnessBodyStat(id);
  };
  const updateFitnessGoals = async (updates) => {
    setFitnessGoals(p => ({ ...(p||{}), ...updates }));
    const { data } = await db.upsertFitnessGoals({ user_id: uid, ...updates });
    if (data?.[0]) setFitnessGoals(data[0]);
  };

  // ── NUTRITION (Fitness & Indian Diet — Phase 1) ─────────────────────────────
  const addDietFood = async (food) => {
    const tmp = { ...food, id: tempId(), user_id: uid, is_global: false, created_at: new Date().toISOString() };
    setDietFoods(p => [...p, tmp]);
    const { data, error } = await db.addDietFood({ ...food, user_id: uid, is_global: false });
    if (error) { console.warn('[addDietFood]', error); setDietFoods(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setDietFoods(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateDietFood = (id, updates) => {
    setDietFoods(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateDietFood(id, updates)).catch(()=>{});
  };
  const deleteDietFood = async (id) => {
    setDietFoods(p => p.filter(x => x.id!==id));
    await db.deleteDietFood(id);
  };

  const addDietMealLog = async (log) => {
    const tmp = { ...log, id: tempId(), user_id: uid, logged_at: new Date().toISOString() };
    setDietMealLogs(p => [tmp, ...p]);
    const { data, error } = await db.addDietMealLog({ ...log, user_id: uid });
    if (error) { console.warn('[addDietMealLog]', error); setDietMealLogs(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setDietMealLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const deleteDietMealLog = async (id) => {
    setDietMealLogs(p => p.filter(x => x.id!==id));
    await db.deleteDietMealLog(id);
  };

  const addDietWaterLog = async (amountMl, logDate) => {
    const tmp = { id: tempId(), user_id: uid, log_date: logDate, amount_ml: amountMl, logged_at: new Date().toISOString() };
    setDietWaterLogs(p => [tmp, ...p]);
    const { data, error } = await db.addDietWaterLog({ user_id: uid, log_date: logDate, amount_ml: amountMl });
    if (error) { console.warn('[addDietWaterLog]', error); setDietWaterLogs(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setDietWaterLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteDietWaterLog = async (id) => {
    setDietWaterLogs(p => p.filter(x => x.id!==id));
    await db.deleteDietWaterLog(id);
  };

  const addDietSupplement = async (supp) => {
    const tmp = { ...supp, id: tempId(), user_id: uid, active: true, created_at: new Date().toISOString() };
    setDietSupplements(p => [...p, tmp]);
    const { data, error } = await db.addDietSupplement({ ...supp, user_id: uid });
    if (error) { console.warn('[addDietSupplement]', error); setDietSupplements(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setDietSupplements(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const updateDietSupplement = (id, updates) => {
    setDietSupplements(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateDietSupplement(id, updates)).catch(()=>{});
  };
  const deleteDietSupplement = async (id) => {
    setDietSupplements(p => p.filter(x => x.id!==id));
    await db.deleteDietSupplement(id);
  };
  const toggleDietSupplementLog = async (supplementId, logDate) => {
    const existing = dietSupplementLogs.find(x => x.supplement_id===supplementId && x.log_date===logDate);
    if (existing) {
      setDietSupplementLogs(p => p.filter(x => x.id!==existing.id));
      await db.deleteDietSupplementLog(existing.id);
      return;
    }
    const tmp = { id: tempId(), user_id: uid, supplement_id: supplementId, log_date: logDate, taken_at: new Date().toISOString() };
    setDietSupplementLogs(p => [...p, tmp]);
    const { data, error } = await db.addDietSupplementLog({ user_id: uid, supplement_id: supplementId, log_date: logDate });
    if (error) { console.warn('[toggleDietSupplementLog]', error); setDietSupplementLogs(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setDietSupplementLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };

  const updateDietTargets = async (updates) => {
    setDietTargets(p => ({ ...(p||{}), ...updates }));
    const { data } = await db.upsertDietTargets({ user_id: uid, ...updates });
    if (data?.[0]) setDietTargets(data[0]);
  };

  const addDietMealPlanItem = async (item) => {
    const tmp = { ...item, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setDietMealPlan(p => [...p, tmp]);
    const { data, error } = await db.addDietMealPlanItem({ ...item, user_id: uid });
    if (error) { console.warn('[addDietMealPlanItem]', error); setDietMealPlan(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setDietMealPlan(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteDietMealPlanItem = async (id) => {
    setDietMealPlan(p => p.filter(x => x.id!==id));
    await db.deleteDietMealPlanItem(id);
  };

  // ── WORKOUTS (Fitness & Indian Diet — Phase 2) ──────────────────────────────
  const addWorkoutExercise = async (ex) => {
    const tmp = { ...ex, id: tempId(), user_id: uid, is_global: false, created_at: new Date().toISOString() };
    setWorkoutExercises(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutExercise({ ...ex, user_id: uid, is_global: false });
    if (error) { console.warn('[addWorkoutExercise]', error); setWorkoutExercises(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setWorkoutExercises(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateWorkoutExercise = (id, updates) => {
    setWorkoutExercises(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateWorkoutExercise(id, updates)).catch(()=>{});
  };
  const deleteWorkoutExercise = async (id) => {
    setWorkoutExercises(p => p.filter(x => x.id!==id));
    await db.deleteWorkoutExercise(id);
  };

  const addWorkoutPlan = async (plan) => {
    const tmp = { ...plan, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutPlans(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutPlan({ ...plan, user_id: uid });
    if (error) { console.warn('[addWorkoutPlan]', error); setWorkoutPlans(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setWorkoutPlans(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateWorkoutPlan = (id, updates) => {
    setWorkoutPlans(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateWorkoutPlan(id, updates)).catch(()=>{});
  };
  const deleteWorkoutPlan = async (id) => {
    setWorkoutPlans(p => p.filter(x => x.id!==id));
    setWorkoutPlanExercises(p => p.filter(x => x.plan_id!==id));
    setWorkoutSchedule(p => p.filter(x => x.plan_id!==id));
    await db.deleteWorkoutPlan(id);
  };

  const addWorkoutPlanExercise = async (item) => {
    const tmp = { ...item, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutPlanExercises(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutPlanExercise({ ...item, user_id: uid });
    if (error) { console.warn('[addWorkoutPlanExercise]', error); setWorkoutPlanExercises(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setWorkoutPlanExercises(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteWorkoutPlanExercise = async (id) => {
    setWorkoutPlanExercises(p => p.filter(x => x.id!==id));
    await db.deleteWorkoutPlanExercise(id);
  };

  const addWorkoutScheduleItem = async (item) => {
    const tmp = { ...item, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutSchedule(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutScheduleItem({ ...item, user_id: uid });
    if (error) { console.warn('[addWorkoutScheduleItem]', error); setWorkoutSchedule(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setWorkoutSchedule(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteWorkoutScheduleItem = async (id) => {
    setWorkoutSchedule(p => p.filter(x => x.id!==id));
    await db.deleteWorkoutScheduleItem(id);
  };

  const addWorkoutLog = async (log) => {
    const tmp = { ...log, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutLogs(p => [tmp, ...p]);
    const { data, error } = await db.addWorkoutLog({ ...log, user_id: uid });
    if (error) { console.warn('[addWorkoutLog]', error); setWorkoutLogs(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setWorkoutLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const updateWorkoutLog = (id, updates) => {
    setWorkoutLogs(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateWorkoutLog(id, updates)).catch(()=>{});
  };
  const deleteWorkoutLog = async (id) => {
    setWorkoutLogs(p => p.filter(x => x.id!==id));
    setWorkoutLogExercises(p => p.filter(x => x.workout_log_id!==id));
    await db.deleteWorkoutLog(id);
  };

  const addWorkoutLogExercise = async (item) => {
    const tmp = { ...item, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutLogExercises(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutLogExercise({ ...item, user_id: uid });
    if (error) { console.warn('[addWorkoutLogExercise]', error); setWorkoutLogExercises(p=>p.filter(x=>x.id!==tmp.id)); return null; }
    if (data?.[0]) setWorkoutLogExercises(p => p.map(x => x.id===tmp.id ? data[0] : x));
    return data?.[0] || tmp;
  };
  const deleteWorkoutLogExercise = async (id) => {
    setWorkoutLogExercises(p => p.filter(x => x.id!==id));
    setWorkoutLogSets(p => p.filter(x => x.workout_log_exercise_id!==id));
    await db.deleteWorkoutLogExercise(id);
  };

  const addWorkoutLogSet = async (set) => {
    const tmp = { ...set, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setWorkoutLogSets(p => [...p, tmp]);
    const { data, error } = await db.addWorkoutLogSet({ ...set, user_id: uid });
    if (error) { console.warn('[addWorkoutLogSet]', error); setWorkoutLogSets(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setWorkoutLogSets(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const updateWorkoutLogSet = (id, updates) => {
    setWorkoutLogSets(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateWorkoutLogSet(id, updates)).catch(()=>{});
  };
  const deleteWorkoutLogSet = async (id) => {
    setWorkoutLogSets(p => p.filter(x => x.id!==id));
    await db.deleteWorkoutLogSet(id);
  };

  // ── BODY COMPOSITION + HEALTH GOALS (Fitness & Indian Diet — Phase 3) ───────
  const updateBodyProfile = async (updates) => {
    setBodyProfile(p => ({ ...(p||{}), ...updates }));
    const { data } = await db.upsertBodyProfile({ user_id: uid, ...updates });
    if (data?.[0]) setBodyProfile(data[0]);
  };

  const addBodyCompositionLog = async (log) => {
    const tmp = { ...log, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setBodyCompositionLogs(p => [tmp, ...p]);
    const { data, error } = await db.addBodyCompositionLog({ ...log, user_id: uid });
    if (error) { console.warn('[addBodyCompositionLog]', error); setBodyCompositionLogs(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setBodyCompositionLogs(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteBodyCompositionLog = async (id) => {
    setBodyCompositionLogs(p => p.filter(x => x.id!==id));
    await db.deleteBodyCompositionLog(id);
  };

  const addHealthGoal = async (goal) => {
    const tmp = { ...goal, id: tempId(), user_id: uid, status: 'active', created_at: new Date().toISOString() };
    setHealthGoals(p => [tmp, ...p]);
    const { data, error } = await db.addHealthGoal({ ...goal, user_id: uid });
    if (error) { console.warn('[addHealthGoal]', error); setHealthGoals(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setHealthGoals(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const updateHealthGoal = (id, updates) => {
    setHealthGoals(p => p.map(x => x.id===id ? { ...x, ...updates } : x));
    Promise.resolve(db.updateHealthGoal(id, updates)).catch(()=>{});
  };
  const deleteHealthGoal = async (id) => {
    setHealthGoals(p => p.filter(x => x.id!==id));
    await db.deleteHealthGoal(id);
  };

  // ── DEEP WORK ──────────────────────────────────────────────────────────────
  const addDeepWorkSession = async (session) => {
    const tmp = { ...session, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setDeepWorkSessions(p => [tmp, ...p]);
    const { data, error } = await db.insertDeepWorkSession({ ...session, user_id: uid });
    if (error) { console.warn('[addDeepWorkSession]', error); setDeepWorkSessions(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setDeepWorkSessions(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteDeepWorkSession = async (id) => {
    setDeepWorkSessions(p => p.filter(x => x.id!==id));
    await db.deleteDeepWorkSession(id);
  };

  // ── CALENDAR EVENTS ────────────────────────────────────────────────────────
  const addCalendarEvent = async (event) => {
    const tmp = { ...event, id: tempId(), user_id: uid, created_at: new Date().toISOString() };
    setCalendarEvents(p => [...p, tmp]);
    const { data, error } = await db.insertCalendarEvent({ ...event, user_id: uid });
    if (error) { console.warn('[addCalendarEvent]', error); setCalendarEvents(p=>p.filter(x=>x.id!==tmp.id)); return; }
    if (data?.[0]) setCalendarEvents(p => p.map(x => x.id===tmp.id ? data[0] : x));
  };
  const deleteCalendarEvent = async (id) => {
    setCalendarEvents(p => p.filter(x => x.id!==id));
    await db.deleteCalendarEvent(id);
  };

  return {
    loading, selDate, setSelDate, uid,
    goals, addGoal, updateGoal, deleteGoal,
    archiveGoal, unarchiveGoal,
    tasks, addTask, updateTask, deleteTask,
    getDailyEntry, updateDailyEntry, loadDailyEntry,
    checkinDefs, addCheckinDef, deleteCheckinDef,
    dailyCi, toggleCi, loadDayCi,
    habits, addHabit, deleteHabit,
    archiveHabit, unarchiveHabit,
    habitLog, toggleHabit, loadHabitLogForDate,
    expenses, addExpense, deleteExpense,
    income, addIncome, deleteIncome,
    fixedIncome, updateFixedIncome,
    ideas, addIdea, updateIdea, deleteIdea,
    books, addBook, updateBook, deleteBook,
    archiveBook, unarchiveBook,
    subs, addSub, updateSub, deleteSub,
    reminders, addReminder, dismissReminder, deleteReminder,
    visions, addVision, updateVision, deleteVision, togglePinVision,
    visionPillars, addVisionPillar, updateVisionPillar, deleteVisionPillar,
    banner, updateBanner, dashboardVisions,
    contentPillars, addContentPillar, deleteContentPillar,
    contentPosts, addContentPost, updateContentPost, deleteContentPost,
    contentStrategy, updateContentStrategy,
    userFiles, uploadUserFile, deleteUserFile,
    weekPlan, updateWeekPlan, loadWeekPlan,
    monthPlan, updateMonthPlan, loadMonthPlan,
    exportBackup, loadAll,
    vaultFolders, addVaultFolder, updateVaultFolder, deleteVaultFolder,
    vaultLinks, addVaultLink, updateVaultLink, deleteVaultLink,
    projects, addProject, updateProject, deleteProject,
    projectTasks, addProjectTask, updateProjectTask, deleteProjectTask,
    projectMembers, inviteProjectMember, acceptProjectInvite, removeProjectMember, assignProjectTask,
    specialDates, addSpecialDate, deleteSpecialDate,
    contentStrategies, addContentStrategy2, updateContentStrategy2, deleteContentStrategy2,
    recurringTasks, addRecurringTask, updateRecurringTask, deleteRecurringTask,
    sharedTemplates, addSharedTemplate, deleteSharedTemplate, hardDeleteSharedTemplate, fetchPublicTemplates, fetchMyTemplates, fetchTemplateById,
    feedPosts, addFeedPost, deleteFeedPost, toggleFeedLike,
    fellows, sendFellowRequest, respondFellowRequest, removeFellow, searchUserByUsername,
    // SaaS Plans
    userPlan, upgradePlan,
    workspaces, addWorkspace, updateWorkspace, deleteWorkspace,
    workspaceMembers, inviteWorkspaceMember, updateWorkspaceMember, removeWorkspaceMember,
    // Payment Automation
    products, addProduct, updateProduct, deleteProduct,
    orders,   addOrder,   updateOrder,
    automations, addAutomation, updateAutomation, deleteAutomation,
    // Services + Biz Profile
    services, addService, updateService, deleteService,
    bizProfile, updateBizProfile,
    // Business Module
    leads, setLeads, addLead, updateLead, deleteLead, convertLeadToClient,
    clients, setClients, addClient, updateClient, deleteClient,
    proposals, setProposals, addProposal, updateProposal, deleteProposal,
    invoices, setInvoices, addInvoice, updateInvoice, deleteInvoice, _nextInvNum,
    sheetLinks, addSheetLink, updateSheetLink, deleteSheetLink,
    consultants, addConsultant, updateConsultant, deleteConsultant,
    // Social Media
    socialAccounts, addSocialAccount, updateSocialAccount, deleteSocialAccount,
    dmTemplates, addDMTemplate, updateDMTemplate, deleteDMTemplate,
    viralLinks, addViralLink, updateViralLink, deleteViralLink,
    engagement, saveEngagement,
    // Meeting Scheduler
    meetingTypes, addMeetingType, updateMeetingType, deleteMeetingType,
    availability, saveAvailability,
    meetings, addMeeting, updateMeeting, deleteMeeting,
    wikiPages, addWikiPage, updateWikiPage, deleteWikiPage,
    knowledgeDomains, addKnowledgeDomain, updateKnowledgeDomain, deleteKnowledgeDomain,
    knowledgeCollections, addKnowledgeCollection, updateKnowledgeCollection, deleteKnowledgeCollection,
    knowledgeTags, getOrCreateKnowledgeTag, deleteKnowledgeTag,
    knowledgeItems, addKnowledgeItem, updateKnowledgeItem, deleteKnowledgeItem, recordKnowledgeItemView,
    knowledgeItemTags, setKnowledgeItemTagList,
    knowledgeRelated, addKnowledgeRelated, deleteKnowledgeRelated,
    knowledgeAttachments, addKnowledgeAttachment, deleteKnowledgeAttachment,
    saveKnowledgeVersion, getKnowledgeVersions,
    integrations, saveIntegration, removeIntegration,
    businessExpenses, addBusinessExpense, deleteBusinessExpense,
    senderProfiles, addSenderProfile, updateSenderProfile, deleteSenderProfile,
    timeLogs, addTimeLog,
    // Tactile Archive new modules
    reflections, addReflection, updateReflection, deleteReflection,
    journalEntries, addJournalEntry, updateJournalEntry, deleteJournalEntry,
    journalTags, getOrCreateJournalTag, deleteJournalTag,
    journalEntryTags, setJournalEntryTagList,
    journalAttachments, addJournalAttachment, deleteJournalAttachment,
    saveJournalVersion, getJournalVersions,
    fitnessMeals, addFitnessMeal, deleteFitnessMeal,
    fitnessBodyStats, addFitnessBodyStat, deleteFitnessBodyStat,
    fitnessGoals, updateFitnessGoals,
    dietFoods, addDietFood, updateDietFood, deleteDietFood,
    dietMealLogs, addDietMealLog, deleteDietMealLog,
    dietWaterLogs, addDietWaterLog, deleteDietWaterLog,
    dietSupplements, addDietSupplement, updateDietSupplement, deleteDietSupplement,
    dietSupplementLogs, toggleDietSupplementLog,
    dietTargets, updateDietTargets,
    dietMealPlan, addDietMealPlanItem, deleteDietMealPlanItem,
    workoutExercises, addWorkoutExercise, updateWorkoutExercise, deleteWorkoutExercise,
    workoutPlans, addWorkoutPlan, updateWorkoutPlan, deleteWorkoutPlan,
    workoutPlanExercises, addWorkoutPlanExercise, deleteWorkoutPlanExercise,
    workoutSchedule, addWorkoutScheduleItem, deleteWorkoutScheduleItem,
    workoutLogs, addWorkoutLog, updateWorkoutLog, deleteWorkoutLog,
    workoutLogExercises, addWorkoutLogExercise, deleteWorkoutLogExercise,
    workoutLogSets, addWorkoutLogSet, updateWorkoutLogSet, deleteWorkoutLogSet,
    bodyProfile, updateBodyProfile,
    bodyCompositionLogs, addBodyCompositionLog, deleteBodyCompositionLog,
    healthGoals, addHealthGoal, updateHealthGoal, deleteHealthGoal,
    deepWorkSessions, addDeepWorkSession, deleteDeepWorkSession,
    calendarEvents, addCalendarEvent, deleteCalendarEvent,
  };
}
